/**
 * offerings/{id} - class & team definitions.
 *
 * One Firestore doc per class the studio runs. Pre-schedule for now -
 * Phase 1-lite is "just enough membership" so we track who teaches what
 * and (next PR) who's enrolled, without committing to a weekly day/time
 * model. That lands in Phase 2C alongside the rooms + transactional
 * reservation model so we build the schedule once, the right way.
 *
 * Fields:
 *   - name       "Tuesday Junior Jazz" (human-friendly label)
 *   - style      Ballet | Jazz | Tap | Hip Hop | Contemporary | Musical
 *                Theatre | Lyrical | Acro | Combo Tap/Ballet | Combo
 *                Comp/Commercial | Glee | Workshop | Other
 *   - level      Level 1 through Level 8 (the studio's own leveling)
 *   - teacherUid uid of an active staff user, or null = unassigned
 *   - studentIds [] for now; the students collection lands next PR and
 *                fills this via the enrollment screen
 *
 * Rules: Director-only read + write (see shared/firestore.rules). We'll
 * widen for staff/family in later phases.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { getAuth, type User } from 'firebase/auth';
import {
  collection,
  doc,
  getDoc,
  getDocFromServer,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  where,
  Timestamp as FirestoreTimestamp,
  type FieldValue,
  type Timestamp,
} from 'firebase/firestore';
import {
  getDownloadURL,
  ref as storageRef,
} from 'firebase/storage';
// Not firebase/storage directly: the wrapper marks an upload in flight so the
// version auto-update cannot reload the tab mid-send (see storageUpload.ts).
import { uploadBytes } from './storageUpload';
import { uploadOutcomeUnknownError } from './uploadCommit';
// uploadClassVideo's bytes (security-audit-2026-09-16 finding H-3: this was
// the one live direct-to-Storage video lane with no App Check, no quota and
// no magic-byte check). AttachmentGatewayError is loaded for its own
// `instanceof` check: a definite, server-decided outcome (quota, magic
// bytes, a dead reservation) is never "may have finished" - only a raw
// network failure with no clean response at all is. Both load on the first
// class video upload, not with the first screen (speed plan 2026-10-19, P2).
const loadClassVideoUploadCode = () =>
  Promise.all([import('./attachmentUploadGateway'), import('./attachmentUploadResume')]);
import type { UploadProgressListener } from './uploadWait';
import { db, storage } from './firebase';
import { onCountableMarks } from './classCancellations';
// Type-only the other way (mediaTagging.ts imports nothing at runtime), so no cycle.
import { isMediaUploadReservation } from './mediaTagging';
// The teacher's note is not on the attendance document any more; see
// subscribeAttendanceNotes for why reading `raw.notes` here was destroying it.
import { attendanceDocId } from './attendanceMarkId';
// The cascade below. classSeries imports reservations; neither imports this
// file, so these two edges are new and acyclic (checked across src/lib on
// 2026-09-15 before they were added, not assumed).
import {
  cancelledReservationIdsFromError,
  deleteClassSeries,
  futureReservationIdsFromRows,
  startOfLocalDay,
} from './classSeries';
// Loaded on the first cancel, not with the first screen (speed plan
// 2026-10-19, P2).
const cancelReservation = async (reservationId: string): Promise<void> => {
  await (await import('./reservations')).cancelReservation(reservationId);
};
import { deleteOfferingBlockedCopy, workshopClassDeleteCopy, type DeleteOfferingCounts } from './deletedClass';
// The one shared reading of a reinstatement Timestamp's civil day, the
// studio's own (America/Chicago) rather than the browser's, so the browser
// and the attendance concerns scan (attendanceConcernsLocal.mjs) can never
// disagree about which day a reinstatement happened on. See that file's
// header for the incident this closes.
import { centralCivilDateFromTimestamp } from '../../netlify/functions/lib/studioCivilDay.mjs';
import { subscribeAttendanceNotes } from './attendance';
import { notifyProductionUpdate } from './notifications';
import { useReadEpoch } from './readEpoch';
import { useAuth } from './auth-context';
import { isHiddenFixtureData } from './smokeFixture';
import type { MessageAttachment } from './threads';
import {
  MusicSaveError,
  writeOfferingMusicViaServer,
  createOfferingViaServer,
  updateOfferingViaServer,
  appendOfferingMediaLinkViaServer,
  finalizeOfferingVideoUploadViaServer,
  promoteOfferingSubSeatViaServer,
  removeOfferingMediaLinkViaServer,
  setOfferingAssistantViaServer,
  archiveClassForDeleteViaServer,
  finishClassDeleteViaServer,
  stopClassDeleteViaServer,
} from './offeringWriteClient';
import {
  normalizeCoTeacherUids,
  type OfferingTeacherFields,
} from './offeringTeachers.ts';
import { assistantAccountLimitError } from './jrStaffRoster.ts';
import { normalizeSubSeats } from './subSeatSchedule.ts';
import type { GuessOffering } from './placeClassGuesses.ts';
import {
  idsStillNeedingResolution,
  widenOfferingsById,
} from './offeringResolution';
import {
  ALTERNATE_LOOKBACK_DAYS,
  computeAlternateStreak,
  type AlternateCountMode,
  type AlternateMark,
  type AlternateReason,
  type AlternateStreak,
} from './alternates';
import { CLASS_PHOTO_MAX_BYTES } from './photoUpload';
import { browserPhotoShrinkDeps, photoShrinkResultOf, shrinkPhotoForUpload } from './photoShrink';
import { notePhotoShrink } from './photoShrinkOutcome';
// Re-exported so every caller resolving "does this offering belong to this
// teacher" or "what should a save write to teacherName" can import them
// alongside isCompetitionOffering/hasRecIdentity below, from the one file
// that owns the OfferingDoc shape. The actual logic lives in its own
// zero-import module (see teacherNameMatch.ts's doc comment) so it can be
// unit-tested directly under plain Node, which this file's own
// firebase/react imports rule out.
export { teacherNameForSave, teacherNameMatchesUser } from './teacherNameMatch';

export const DANCE_STYLES = [
  'Ballet',
  'Jazz',
  'Tap',
  'Hip Hop',
  'Contemporary',
  'Musical Theatre',
  'Lyrical',
  'Acro',
  'Combo Tap/Ballet',
  'Combo Contemporary/Commercial',
  'Combo Comp/Commercial',
  'Glee',
  'Workshop',
  'Other',
] as const;
export type DanceStyle = string;

// Levels are the studio's own 1-8 leveling. Stored as the display label so
// every screen (dropdowns, pills, rosters) reads correctly with no extra
// formatting code.
export const LEVELS = [
  'Level 1',
  'Level 2',
  'Level 3',
  'Level 4',
  'Level 5',
  'Level 6',
  'Level 7',
  'Level 8',
] as const;
export type Level = string;

export type OfferingKind = 'class' | 'comp-class' | 'comp-dance' | 'workshop';
export type MusicReviewStatus = 'pending' | 'approved' | 'changes-needed';

/**
 * Comp day look ~ hair, makeup, lipstick, shoes, plus a free-text notes
 * field. Optional strings, all optional; the whole map is optional so old
 * offering docs (created before this field existed) are untouched. The
 * frozen Helper app will pull this later via the helper-hub migration
 * bridge (docs/helper-hub-migration-plan.md); for now the Hub is just the
 * store-and-edit surface.
 */
export interface CompLook {
  hair?: string;
  makeup?: string;
  lipstick?: string;
  shoes?: string;
  notes?: string;
}

const COMP_LOOK_FIELD_MAX = 500;

function cleanCompLookField(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, COMP_LOOK_FIELD_MAX) : undefined;
}

/** Reads a raw compLook value off a doc, dropping empty/invalid fields. */
export function normalizeCompLook(value: unknown): CompLook | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const raw = value as Record<string, unknown>;
  const hair = cleanCompLookField(raw.hair);
  const makeup = cleanCompLookField(raw.makeup);
  const lipstick = cleanCompLookField(raw.lipstick);
  const shoes = cleanCompLookField(raw.shoes);
  const notes = cleanCompLookField(raw.notes);
  if (!hair && !makeup && !lipstick && !shoes && !notes) return undefined;
  return {
    ...(hair ? { hair } : {}),
    ...(makeup ? { makeup } : {}),
    ...(lipstick ? { lipstick } : {}),
    ...(shoes ? { shoes } : {}),
    ...(notes ? { notes } : {}),
  };
}

/** True once any comp day look field has been filled in for this offering. */
export function hasCompLook(offering: Pick<OfferingDoc, 'compLook'>): boolean {
  const look = offering.compLook;
  return Boolean(look && (look.hair || look.makeup || look.lipstick || look.shoes || look.notes));
}

/**
 * Why one dancer on this roster is standing by, and the paper trail behind it.
 *
 * Keyed by studentId in `offerings.alternateInfo`. The dancer stays in
 * `studentIds` either way - see lib/alternates.ts for why alternate is an
 * attribute of a roster spot rather than a second roster.
 *
 * An entry SURVIVES reinstatement. Once `reinstatedAt` is set the dancer is no
 * longer in `alternateStudentIds`, and the entry becomes the record of what
 * happened plus the streak baseline: nothing on or before that date can flag
 * them again. Deleting the entry on reinstatement would throw the baseline
 * away and the same three absences would move them straight back.
 *
 * PRIVACY, READ THIS BEFORE ADDING A FIELD. This map lives on the offering
 * doc, which EVERY active account can read, families included. It is keyed by
 * studentId, and a family can only resolve a studentId to a name for their own
 * dancers, so the practical exposure is small - but `reinstateNote` is free
 * text about a named child. Keep anything written here operational and neutral
 * ("learned the combo, made up Tuesday"), never a judgement, and do not add a
 * field that would be worse in the wrong hands. If richer notes are ever
 * wanted, they belong in a staff-only collection like offeringStaffNotes, not
 * here.
 */
export interface AlternateInfo {
  reason: AlternateReason;
  /** When this alternate standing began. */
  since: Timestamp | FieldValue;
  /** uid of whoever set it. */
  setBy: string;
  /** For an attendance move, the exact dates behind it. Kim's evidence. */
  causedByDates?: string[];
  /** Kim's short note when she approves them back. */
  reinstateNote?: string;
  reinstatedBy?: string;
  /** Set only once they are back to a full dancer. Doubles as the baseline. */
  reinstatedAt?: Timestamp | FieldValue;
}

export interface DanceTrack {
  id: string;
  label: string;
}

export const MUSIC_REVIEW_LABELS: Record<MusicReviewStatus, string> = {
  pending: 'Waiting on Kim',
  approved: 'Approved',
  'changes-needed': 'Needs changes',
};

/**
 * One music or video link on a class. A class can have several (one per
 * dance), so these live in arrays on the offering. `addedAt` is epoch millis
 * set client-side - Firestore won't allow serverTimestamp() inside an array,
 * and we only need it to sort newest-first and highlight the latest upload.
 */
/**
 * What a piece of music or video is FOR. Bobbie's categories: a regular
 * class track, recital music, or the comp song. Older links have no
 * category and simply show no badge.
 */
// 'eval' joined 2026-09-01 (Bobbie: "a video upload specifically for eval").
// Staff-only intent, families never see an eval-tagged link: MediaPanel's
// family branch, every family/dancer/comp-board surface and both public
// Netlify functions filter it out - see familyVisibleMediaLinks in
// mediaTagging.ts, the one place that filter is defined.
export type MediaCategory = 'class' | 'recital' | 'comp' | 'eval';

// Bobbie's selection labels, 2026-08-06: "Classroom Non-Performance /
// Recital Dance / Comp Dance". Stored values are unchanged; only the words
// moved. Classroom music is the label that keeps a track OFF the Production
// Tracker's missing counts, so it has to say plainly that it does not
// perform.
export const MEDIA_CATEGORY_LABELS: Record<MediaCategory, string> = {
  class: 'Classroom (non-performance)',
  recital: 'Recital dance',
  comp: 'Comp dance',
  eval: 'Eval (staff only)',
};

export function normalizeMediaCategory(value: unknown): MediaCategory | undefined {
  return value === 'class' || value === 'recital' || value === 'comp' || value === 'eval' ? value : undefined;
}

/** Sensible starting category when adding media to an offering. */
export function defaultMediaCategoryForOffering(offering: Pick<OfferingDoc, 'kind' | 'name'> | null | undefined): MediaCategory {
  return offering && isCompetitionOffering(offering as OfferingDoc) ? 'comp' : 'class';
}

/**
 * "Posted 6.4.26" style stamp from the link's addedAt millis, on the STUDIO's
 * day (bug B34). Lives in lib/mediaShelfDisplay.ts so a test can run it.
 */
export { mediaPostedLabel } from './mediaShelfDisplay.ts';

export interface MediaLink {
  id: string;
  url: string;
  label: string;
  addedAt: number;
  category?: MediaCategory;
  addedByName?: string;
  /** Display hint only; approval authority is held in the private server ledger. */
  addedByUid?: string;
  /**
   * Who added this link, so publicClass.mjs's public class page can tell a
   * staff-posted video/music link apart from a family or dancer upload and
   * keep the latter off the unauthenticated page (security-audit-2026-09-02
   * S1-1). Every path in this file stamps 'staff' - makeMediaLink and
   * uploadClassVideo are only ever reached from staff-facing screens
   * (MediaPanel.tsx, Offering.tsx, the Fable assistant). 'family' and
   * 'dancer' are stamped server-side, by familyClassVideo.mjs and
   * dancerClassMedia.mjs through classMediaCommit.mjs's
   * buildClassVideoLinkEntry - never from this client file. WHITELISTED
   * in normalizeMediaLink below on purpose, same trap this file already
   * documents for storagePath/r2Key: a field this normalizer does not name
   * is stripped off every entry the next time anyone rewrites the array
   * (a final-cut toggle, a relabel), which would silently drop a family or
   * dancer entry back onto the storagePath-prefix fallback the public page
   * also honors - safe, but not the point of having the field at all.
   */
  addedByRole?: 'staff' | 'family' | 'dancer';
  danceTrackId?: string | null;
  danceTrackLabel?: string | null;
  /**
   * The song's artist, typed beside Song title on a MUSIC entry only.
   * Optional: an older entry with none is normal and is never backfilled with
   * a guess. Kept on its own (not only folded into the label) so it still
   * shows after a Custom name overrides the generated label. It lives inside
   * the array entry, which the music gateway (offeringWrite) owns, so it costs
   * no rules bytes. Every whitelist that rebuilds a link must carry it.
   */
  artist?: string;
  storagePath?: string;
  contentType?: string;
  fileName?: string;
  /**
   * The uploading File's own `size` and `lastModified`, stamped on a video
   * upload's PLACEHOLDER reservation entry (round five P3, Opus refute
   * round 3). `attachmentUploadResume.ts`'s stored-request-id key is built
   * from exactly this triple (name, size, lastModified) - a "Finish
   * linking" or "Check uploaded file" recovery press only has this
   * reservation row to work from, never the original File object (the tab
   * that started the transfer may be long gone), so without these two
   * fields a recovery commit could never find and clear its own stored id,
   * leaving it to resume an already-consumed reservation on the family's
   * next attempt and fail with attachment_already_used. WHITELISTED HERE ON
   * PURPOSE, same trap as fileName above: dropped by a relabel and a later
   * recovery loses the only way to clear its own stale id.
   */
  fileSize?: number;
  fileLastModified?: number;
  /**
   * The `classPosts` document this clip was mirrored from, when a video
   * attached to a CLASS BOARD post put itself on this shelf (Bobbie,
   * 2026-09-15: "videos in posts are not showing up under videos and music
   * section so they are not showing up for younger kids"). Written only by
   * netlify/functions/lib/classPostVideoMirror.mjs, never by a client, and
   * stripped off any client-supplied link in lib/offeringWrite.mjs's
   * staffVideoLink exactly the way addedByUid and addedByRole are.
   *
   * It is the link that makes removal work BOTH WAYS (Bobbie's mirror ruling
   * 2, 2026-09-09): taking the post back removes this entry, and removing
   * this entry takes the clip off that post while the post's words stay.
   * Absent on every entry a teacher, parent or dancer added on Music & Video
   * itself, and absent means exactly that: no post on the other side.
   *
   * WHITELISTED in normalizeMediaLink below on purpose, the same trap this
   * file already documents for storagePath, r2Key and addedByRole: a field
   * the normalizer does not name is stripped off every entry the next time
   * anyone rewrites the array (a relabel, a final-cut toggle), and a mirrored
   * entry that quietly lost this marker would stop removing both ways with
   * nothing on screen admitting it.
   */
  sourcePostId?: string;
  /**
   * Which shelf the bytes live on (DECISIONS.md, "Cloudflare R2 is the video
   * home"). Absent (or any value other than 'r2') means GCS - today's shape,
   * completely unchanged: url/storagePath keep resolving and rendering
   * exactly as before. 'r2' means the bucket is PRIVATE, so `url` is never a
   * playable link on this kind of entry - it is a placeholder that only
   * exists so normalizeMediaLink's isSafeMediaUrl check still passes. The
   * REAL playable URL comes from hub/src/lib/videoPlayback.ts, which every
   * render call site (FamilyVideos, MediaPanel, ClassFeed) uses instead of
   * reading `url` directly on a video link.
   */
  storage?: 'r2';
  /** The R2 object key. Present only when storage === 'r2'. */
  r2Key?: string;
  /**
   * The R2 key of the TRANSCODED 1080p rendition, once the migration sweep has
   * copied it across (netlify/functions/lib/r2VideoMigration.mjs's stream
   * lane). Present only when storage === 'r2' AND a transcode actually
   * finished. This is what playback asks the presign lane for; r2Key above
   * stays the original and is what the Save button hands a family.
   *
   * Bobbie, 2026-08-28: *"It is loading too slowly for many people. And videos
   * stopped playing in app like they are supposed to and now just
   * downloads."* Both halves were the same gap. Before this field existed an
   * R2-homed video served its raw original forever - up to the 6 GB upload
   * cap, pulled in full on every watch - and served it under the original's
   * own extension, so a .m4v or .3gp video also lost its in-app player, since
   * the 1080p rendition the app used to play is always an .mp4.
   *
   * Absent means exactly what it has always meant: play the original. A
   * transcode that failed, was skipped as not-smaller, is still blocked on the
   * API toggle, or never ran, all read the same way, and all still work.
   */
  r2StreamKey?: string;
  /**
   * "Is this the final cut and performance ready?" Meaningful on any link
   * (music or video) whose category is recital or comp; classroom media is
   * never asked (Bobbie, 2026-08-06). Music starts unapproved; another active
   * staff member marks it final through the server's verified uploader check.
   * Video keeps its existing add-time choice and assigned-editor controls.
   * Absent means not final. The flag lives inside the media array entry.
   */
  finalCut?: boolean;
  /**
   * The shared, everyday-watching 1080p copy the transcode sweep produces
   * from a GCS-homed original (docs/video-transcode-plan-2026-08-18.md,
   * DECISIONS.md's "Transcode approved" entry). Written by
   * netlify/functions/videoTranscodeSweep-background.mjs only, never by any
   * client write path. Absent means either the entry is not GCS-homed, or a
   * transcode has not finished yet - both read the same as "keep using
   * url", see playbackUrl() below.
   */
  streamUrl?: string;
  /** The transcoded copy's own Storage object path, alongside streamUrl for
   * the same reasons storagePath sits alongside url. Purely informational
   * today; nothing reads it back out yet, but it is cheap to keep and saves
   * a later feature from having to derive it. */
  streamPath?: string;
  /**
   * Where this entry's transcode stands. Undefined means "never attempted"
   * (the common case for most of this app's life, and permanently the case
   * for anything that is not GCS-homed). 'blocked_api_disabled' means the
   * sweep found the Transcoder API not yet enabled and will keep retrying
   * automatically, at no cost to the original. 'failed' is terminal, after
   * the sweep's attempts cap; the player then keeps using the original
   * silently, forever, same as if this field had never existed.
   * 'skipped_not_smaller' is also terminal: the job succeeded but the 1080p
   * output was not meaningfully smaller than the original (an
   * already-small or already-1080p-or-below source, most likely), so the
   * sweep never wrote streamUrl and never will re-enqueue it - "never
   * prefer a worse copy" over "keep trying to shrink the unshrinkable".
   */
  transcodeState?: 'creating' | 'staging' | 'queued' | 'done' | 'blocked_api_disabled' | 'failed'
    | 'skipped_not_smaller';
  /**
   * The GCS object path of the TEMPORARY copy the transcode sweep stages for
   * a video that lives only on Cloudflare R2. The Transcoder API reads GCS
   * and nothing else, and a parent or older dancer upload has gone straight
   * to R2 since 2026-09-02, so one is copied in first and the job reads that
   * (Bobbie approved the spend on 2026-09-10, "Transcode parent and dancer
   * uploads too"). Always under transcode-input/, written and deleted only by
   * netlify/functions/videoTranscodeSweep-background.mjs, never by a client.
   *
   * Absent means either the entry never needed one or the sweep has already
   * cleaned it up once the smaller copy was safely in R2. It is NOT the
   * original and never stands in for one: url, storagePath, storage, r2Key
   * and r2StreamKey are all untouched by that lane.
   *
   * WHITELISTED HERE ON PURPOSE, the same trap as the fields above: dropped
   * by a relabel, the sweep would lose track of a multi-GB temp object that
   * nothing would then delete, and re-copy it on the next run.
   */
  transcodeSourcePath?: string;
  /**
   * Bookkeeping the sweep needs to resume across runs (the in-flight
   * Transcoder job's resource name, and how many real attempts have been
   * spent) - not rendered anywhere, but WHITELISTED here anyway. Every
   * client-side rewrite of a videoLinks array (toggleMediaLinkFinalCut's
   * transformOfferingMediaLinks, for one) reads the whole array through
   * normalizeMediaLink and writes it whole back; a field left off this
   * interface (and the normalizer below) would be silently stripped off
   * EVERY entry the next time anyone toggled a final cut in that class,
   * corrupting the sweep's own progress mid-flight. This is the exact
   * "whitelist trap" the transcode plan names - see normalizeMediaLink.
   */
  transcodeJobName?: string;
  transcodeAttempts?: number;
  /**
   * How many times the sweep has tried to make a working copy of an R2-only
   * video so the Transcoder can read it. SEPARATE from transcodeAttempts on
   * purpose: that one bounds spending (three paid jobs), this one bounds
   * copying (three multi-GB copies), and a video must never lose a paid
   * attempt to a copy that failed. Charged before any bytes move, so a run
   * the platform kills still leaves a mark instead of re-copying for ever.
   * Whitelisted for the same reason every field above it is.
   */
  transcodeStageAttempts?: number;
  /**
   * Why the lane gave up, in plain words, written once alongside a terminal
   * transcodeState of 'failed'. Absent on a 'failed' that came from three
   * failed Transcoder jobs; present when the working copy never finished.
   * Same state, two different stories, and the row should say which.
   */
  transcodeFailureReason?: string;
  /**
   * The sweep's create-claim lease: written with transcodeState 'creating'
   * BEFORE the paid Transcoder call and cleared the moment its outcome is
   * recorded. Whitelisted for exactly the reason the two fields above are.
   * Stripping these would put a claim back in play early and could buy the
   * same job twice.
   */
  transcodeCreateLeaseId?: string;
  transcodeCreateLeaseAt?: number;
}

/**
 * Which URL actually plays, for a GCS entry. No screen imports this any more
 * (every player goes through videoPlayback.ts), but it is kept in step with
 * immediatePlaybackUrl there so nothing can quietly bypass the 2026-09-18
 * PLAYBACK HOLD: the ORIGINAL plays, and the transcoded 1080p copy is only a
 * fallback for an entry with no url at all. Before the hold this preferred
 * the copy (smaller, faster on venue wifi and phones); it goes back to that
 * with videoPlayback.ts once the copies are re-made sharp.
 */
export function playbackUrl(link: Pick<MediaLink, 'url' | 'streamUrl'>): string {
  return link.url || link.streamUrl || '';
}

/**
 * Media links render as family-facing <a href> targets, so only real web
 * URLs are allowed. A teacher-written `javascript:`/`data:`/`vbscript:`
 * value would otherwise execute in a family's session. Uploaded media
 * always yields an https Firebase download URL, so this only ever rejects
 * hand-pasted hostile links.
 */
export function isSafeMediaUrl(url: string): boolean {
  return /^https?:\/\/.+/i.test(url.trim());
}

function normalizeMediaLink(link: unknown, index: number): MediaLink | null {
  if (!link || typeof link !== 'object') return null;
  const candidate = link as Partial<MediaLink>;
  if (typeof candidate.url !== 'string' || !candidate.url.trim()) return null;
  const url = candidate.url.trim();
  // Drop any link that is not a plain http(s) URL so a hostile scheme
  // never renders even if it slipped into an older doc.
  if (!isSafeMediaUrl(url)) return null;
  const label = typeof candidate.label === 'string' ? candidate.label.trim() : '';
  // WHITELISTED HERE ON PURPOSE: a relabel or final-cut toggle reads the whole
  // array through this normalizer, so leaving artist off would strip it from
  // every entry the next time anyone touched one.
  const artist = typeof candidate.artist === 'string' ? candidate.artist.trim() : '';
  const id = typeof candidate.id === 'string' && candidate.id.trim()
    ? candidate.id.trim()
    : `media-${index}-${url}`;
  const addedAt = typeof candidate.addedAt === 'number' && Number.isFinite(candidate.addedAt)
    ? candidate.addedAt
    : 0;
  const danceTrackId = typeof candidate.danceTrackId === 'string' ? candidate.danceTrackId.trim() : undefined;
  const danceTrackLabel = typeof candidate.danceTrackLabel === 'string' ? candidate.danceTrackLabel.trim() : undefined;
  const storagePath = typeof candidate.storagePath === 'string' ? candidate.storagePath.trim() : undefined;
  const contentType = typeof candidate.contentType === 'string' ? candidate.contentType.trim() : undefined;
  const fileName = typeof candidate.fileName === 'string' ? candidate.fileName.trim() : undefined;
  // WHITELISTED HERE ON PURPOSE, same trap as fileName above: a stored
  // upload recovery's ONLY way to find and clear its own localStorage
  // request id (round five P3). Dropped by a relabel or a final-cut toggle
  // and a "Finish linking" press after that can never clear it.
  const fileSize = typeof candidate.fileSize === 'number' && Number.isFinite(candidate.fileSize)
    ? candidate.fileSize
    : undefined;
  const fileLastModified = typeof candidate.fileLastModified === 'number'
    && Number.isFinite(candidate.fileLastModified)
    ? candidate.fileLastModified
    : undefined;
  // WHITELISTED HERE ON PURPOSE, same trap as storagePath/addedByRole above:
  // the marker that makes a mirrored board clip removable from both sides.
  // Dropped here and a relabel would quietly orphan the post's copy.
  const sourcePostId = typeof candidate.sourcePostId === 'string' ? candidate.sourcePostId.trim() : undefined;
  const category = normalizeMediaCategory(candidate.category);
  const addedByName = typeof candidate.addedByName === 'string' ? candidate.addedByName.trim() : undefined;
  // WHITELISTED HERE ON PURPOSE, same trap as storagePath/r2Key below: an
  // unrecognized value is dropped rather than passed through, so a bad
  // write can never mint an untrusted 'staff' the public page would trust.
  const addedByRole = candidate.addedByRole === 'staff' || candidate.addedByRole === 'family'
    || candidate.addedByRole === 'dancer'
    ? candidate.addedByRole
    : undefined;
  // streamUrl gets the exact same hostile-scheme guard as url, dropping just
  // the field (never the whole entry - the original url is still good) if
  // it is not a plain http(s) link.
  const streamUrlRaw = typeof candidate.streamUrl === 'string' ? candidate.streamUrl.trim() : '';
  const streamUrl = streamUrlRaw && isSafeMediaUrl(streamUrlRaw) ? streamUrlRaw : undefined;
  const streamPath = typeof candidate.streamPath === 'string' ? candidate.streamPath.trim() : undefined;
  const transcodeState = candidate.transcodeState === 'creating' || candidate.transcodeState === 'staging'
    || candidate.transcodeState === 'queued'
    || candidate.transcodeState === 'done'
    || candidate.transcodeState === 'blocked_api_disabled' || candidate.transcodeState === 'failed'
    || candidate.transcodeState === 'skipped_not_smaller'
    ? candidate.transcodeState
    : undefined;
  // WHITELISTED HERE ON PURPOSE, same trap as the transcode bookkeeping
  // above: the pointer to the temp copy the R2 lane staged into GCS. Dropped
  // here and a relabel would strand a multi-GB object nothing ever deletes.
  const transcodeSourcePath = typeof candidate.transcodeSourcePath === 'string'
    ? candidate.transcodeSourcePath.trim()
    : undefined;
  const transcodeJobName = typeof candidate.transcodeJobName === 'string'
    ? candidate.transcodeJobName.trim()
    : undefined;
  const transcodeAttempts = typeof candidate.transcodeAttempts === 'number' && Number.isFinite(candidate.transcodeAttempts)
    ? candidate.transcodeAttempts
    : undefined;
  // WHITELISTED HERE ON PURPOSE, same trap as transcodeAttempts above: the
  // copy counter is what stops an R2 video whose working copy never finishes
  // from re-copying gigabytes for ever. Stripped by a relabel, the cap resets
  // to zero and the loop starts again.
  const transcodeStageAttempts = typeof candidate.transcodeStageAttempts === 'number'
    && Number.isFinite(candidate.transcodeStageAttempts)
    ? candidate.transcodeStageAttempts
    : undefined;
  const transcodeFailureReason = typeof candidate.transcodeFailureReason === 'string'
    ? candidate.transcodeFailureReason.trim()
    : undefined;
  const transcodeCreateLeaseId = typeof candidate.transcodeCreateLeaseId === 'string'
    ? candidate.transcodeCreateLeaseId.trim()
    : undefined;
  const transcodeCreateLeaseAt = typeof candidate.transcodeCreateLeaseAt === 'number'
    && Number.isFinite(candidate.transcodeCreateLeaseAt)
    ? candidate.transcodeCreateLeaseAt
    : undefined;
  // R2 home (DECISIONS.md, "Cloudflare R2 is the video home"). Only the
  // literal 'r2' is ever trusted; r2Key only carries through when storage
  // actually is 'r2', so a stray r2Key on an otherwise-GCS entry (should
  // never happen server-side, but this normalizer never trusts shape it did
  // not itself just checked) can never make a GCS link look R2-homed.
  const storage = candidate.storage === 'r2' ? 'r2' : undefined;
  const r2Key = storage === 'r2' && typeof candidate.r2Key === 'string' && candidate.r2Key.trim()
    ? candidate.r2Key.trim()
    : undefined;
  // Same rule, same reason, for the transcoded rendition's key: only carried
  // through when storage actually is 'r2'. WHITELISTED HERE ON PURPOSE - a
  // field this normalizer does not name is stripped off every entry in the
  // class the next time anyone rewrites the array (toggling a final cut, for
  // one), which would silently undo the migration sweep's work and put every
  // family back on the raw original.
  const r2StreamKey = storage === 'r2'
    && typeof candidate.r2StreamKey === 'string' && candidate.r2StreamKey.trim()
    ? candidate.r2StreamKey.trim()
    : undefined;
  return {
    id,
    url,
    label,
    addedAt,
    ...(category ? { category } : {}),
    ...(addedByRole ? { addedByRole } : {}),
    ...(addedByName ? { addedByName } : {}),
    ...(artist ? { artist } : {}),
    ...(typeof candidate.addedByUid === 'string' ? { addedByUid: candidate.addedByUid } : {}),
    ...(danceTrackId ? { danceTrackId } : {}),
    ...(danceTrackLabel ? { danceTrackLabel } : {}),
    ...(storagePath ? { storagePath } : {}),
    ...(contentType ? { contentType } : {}),
    ...(fileName ? { fileName } : {}),
    ...(fileSize !== undefined ? { fileSize } : {}),
    ...(fileLastModified !== undefined ? { fileLastModified } : {}),
    ...(sourcePostId ? { sourcePostId } : {}),
    ...(storage ? { storage } : {}),
    ...(r2Key ? { r2Key } : {}),
    ...(r2StreamKey ? { r2StreamKey } : {}),
    ...(candidate.finalCut === true ? { finalCut: true } : {}),
    ...(streamUrl ? { streamUrl } : {}),
    ...(streamPath ? { streamPath } : {}),
    ...(transcodeState ? { transcodeState } : {}),
    ...(transcodeSourcePath ? { transcodeSourcePath } : {}),
    ...(transcodeJobName ? { transcodeJobName } : {}),
    ...(transcodeAttempts ? { transcodeAttempts } : {}),
    ...(transcodeStageAttempts ? { transcodeStageAttempts } : {}),
    ...(transcodeFailureReason ? { transcodeFailureReason } : {}),
    ...(transcodeCreateLeaseId ? { transcodeCreateLeaseId } : {}),
    ...(transcodeCreateLeaseAt ? { transcodeCreateLeaseAt } : {}),
  };
}

/** Newest-first copy of a media-link list (does not mutate the input). */
export function sortMediaNewestFirst(links: readonly unknown[] | null | undefined): MediaLink[] {
  if (!Array.isArray(links)) return [];
  return links
    .map((link, index) => normalizeMediaLink(link, index))
    .filter((link): link is MediaLink => Boolean(link))
    .sort((a, b) => (b.addedAt ?? 0) - (a.addedAt ?? 0));
}

/** Make a new MediaLink with a unique id + current timestamp. */
export function makeMediaLink(
  url: string,
  label: string,
  danceTrack?: DanceTrack | null,
  extra?: { category?: MediaCategory; addedByName?: string; finalCut?: boolean; artist?: string }
): MediaLink {
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const addedByName = extra?.addedByName?.trim();
  const artist = extra?.artist?.trim().slice(0, 240);
  return {
    id,
    url: url.trim(),
    label: label.trim(),
    addedAt: Date.now(),
    // Every caller of makeMediaLink is a staff-facing screen; see the
    // addedByRole doc comment on MediaLink above.
    addedByRole: 'staff',
    ...(extra?.category ? { category: extra.category } : {}),
    ...(addedByName ? { addedByName } : {}),
    ...(artist ? { artist } : {}),
    ...(danceTrack ? { danceTrackId: danceTrack.id, danceTrackLabel: danceTrack.label } : {}),
    ...(extra?.finalCut === true ? { finalCut: true } : {}),
  };
}

/**
 * A copy of `links` with one entry's final-cut flag set or cleared. Pure so
 * the tracker and the class page share one implementation and the tests can
 * pin it. Clearing DELETES the key rather than writing false, keeping older
 * docs byte-stable with the entries this app has always written.
 */
export function withMusicLinkFinalCut(
  links: readonly MediaLink[],
  linkId: string,
  finalCut: boolean
): MediaLink[] {
  return links.map((link) => {
    if (link.id !== linkId) return link;
    const { finalCut: _prior, ...rest } = link;
    return finalCut ? { ...rest, finalCut: true } : rest;
  });
}

/**
 * Cap on an edited media label's length. Add-time labels carry no cap of
 * their own to match: not the generated "<song> - <cut>" name, not the
 * free-text "Custom name" input in MediaPanel.tsx (no maxLength attribute),
 * and not shared/firestore.rules' teacherChangedFieldsValid, which validates
 * musicLinks/videoLinks by LIST shape and size only, never a per-entry
 * field. Rather than invent a number, this matches the one real free-text
 * cap already living in the same panel: MediaPanel.tsx's family-note input,
 * maxLength={240}.
 */
export const MEDIA_LABEL_MAX = 240;

/**
 * A copy of `links` with one entry's label set, trimmed and capped at
 * MEDIA_LABEL_MAX. Pure and mirrors withMusicLinkFinalCut above, so the
 * relabel editor and its tests share one implementation. A blank (or
 * whitespace-only) newLabel is refused: the list comes back UNCHANGED so an
 * empty save keeps the entry's old label rather than blanking a row nobody
 * could then read. Only the label changes - category, finalCut, danceTrackId
 * and every other field on the entry (and on every sibling entry) pass
 * through untouched.
 */
export function withMediaLinkLabel(
  links: readonly MediaLink[],
  linkId: string,
  newLabel: string
): MediaLink[] {
  const trimmed = newLabel.trim().slice(0, MEDIA_LABEL_MAX);
  if (!trimmed) return links.slice();
  return links.map((link) => (link.id === linkId ? { ...link, label: trimmed } : link));
}

/**
 * Every media-link EDIT (final cut, relabel, remove, the editor dialog) goes
 * through a fresh read rather than writing the caller's own copy of the
 * array back. The caller's copy is a snapshot, and the moment somebody else's
 * upload appends to the real array (appendOfferingMediaLink's arrayUnion), a
 * whole-array write computed from that snapshot ERASES the new upload - the
 * emulator reproduced exactly that data loss. Inside runTransaction the read
 * is fresh and the commit fails-and-retries if the doc moved, so the pure
 * transform always applies to what is actually stored. Transforms must stay
 * pure: the SDK re-runs them on contention.
 *
 * Music uses a server revision and retries a changed revision; video uses the
 * existing Firestore transaction. The music gateway retains the legacy
 * musicReviewStatus pending value while enforcing final-cut provenance.
 */
/**
 * Apply only the requested edits, keeping unrelated raw metadata and legacy
 * rows. BOTH lists go through this now, not only music (audit lane H, finding
 * H-4, 2026-09-17). The video branch used to write normalizeMediaLink's
 * output straight back, which meant every field the normalizer does not name
 * was stripped off EVERY entry in the class the next time anyone fixed a
 * typo: the nine r2VideoMigration.mjs bookkeeping fields went that way, and
 * with them the in-flight multipart uploadId the migration needs to abort,
 * the attempts cap, and the number videoCostReport prices Bobbie's R2
 * decision from. Whitelisting today's known names would have fixed today and
 * re-armed the same trap for the next server-written field, so the video
 * branch preserves unknown fields instead, exactly as music already did.
 */
export function preserveMediaTransform(raw: unknown[], before: MediaLink[], after: MediaLink[]): unknown[] {
  const remaining = new Map(after.map(entry => [entry.id, entry]));
  const previous = new Map(before.map(entry => [entry.id, entry]));
  const result: unknown[] = [];
  raw.forEach((entry, index) => {
    const normalized = normalizeMediaLink(entry, index);
    if (!normalized) { result.push(entry); return; }
    const next = remaining.get(normalized.id);
    if (!next) return;
    remaining.delete(normalized.id);
    const prior = previous.get(normalized.id) ?? normalized;
    if (JSON.stringify(prior) === JSON.stringify(next)) { result.push(entry); return; }
    const merged: Record<string, unknown> = { ...(entry as Record<string, unknown>) };
    for (const key of new Set([...Object.keys(prior), ...Object.keys(next)])) {
      const beforeValue = (prior as unknown as Record<string, unknown>)[key];
      const afterValue = (next as unknown as Record<string, unknown>)[key];
      if (JSON.stringify(beforeValue) === JSON.stringify(afterValue)) continue;
      if (afterValue === undefined) delete merged[key];
      else merged[key] = afterValue;
    }
    if (!merged.id) merged.id = normalized.id;
    result.push(merged);
  });
  return [...result, ...remaining.values()];
}
async function sendMusicRequest(body: Record<string, unknown>): Promise<{ links?: unknown[]; revision?: string }> {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Sign in again to save music.');
  return writeOfferingMusicViaServer(user, body);
}

function isPendingMusicLink(link: Pick<MediaLink, 'url'>): boolean {
  try { return new URL(link.url).pathname === '/media-upload-pending'; } catch { return false; }
}

export async function transformOfferingMediaLinks(
  offeringId: string,
  kind: 'music' | 'video',
  transform: (links: MediaLink[]) => MediaLink[]
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  if (kind === 'music') {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const page = await sendMusicRequest({ action: 'readMusic', offeringId });
      if (!Array.isArray(page.links) || typeof page.revision !== 'string') throw new MusicSaveError('music_save_failed');
      const before = sortMediaNewestFirst(page.links);
      const transformed = transform(before);
      const beforeById = new Map(before.map((link) => [link.id, link]));
      const newlyPublished = transformed.filter((link) => {
        if (isPendingMusicLink(link) || link.finalCut === true) return false;
        const previous = beforeById.get(link.id);
        return !previous || isPendingMusicLink(previous);
      });
      const next = preserveMediaTransform(page.links, before, transformed);
      try {
        await sendMusicRequest({ action: 'replaceMusic', offeringId, links: next, revision: page.revision });
        for (const link of newlyPublished) {
          await notifyProductionUpdate(offeringId, 'music-upload', link.id);
        }
        return;
      } catch (error) {
        if (!(error instanceof MusicSaveError) || error.code !== 'music_changed' || attempt === 2) throw error;
      }
    }
    return;
  }
  const database = db;
  await runTransaction(database, async (txn) => {
    const ref = doc(database, 'offerings', offeringId);
    const snap = await txn.get(ref);
    if (!snap.exists()) throw new Error('This class no longer exists.');
    const raw = snap.data() as { musicLinks?: unknown[]; videoLinks?: unknown[] };
    const rawVideoLinks = Array.isArray(raw.videoLinks) ? raw.videoLinks : [];
    const links = sortMediaNewestFirst(rawVideoLinks);
    const next = transform(links);
    // Merged onto the RAW array, never written as the normalized one: see
    // preserveMediaTransform above. A relabel, a final-cut toggle or a remove
    // changes only what it meant to change, and every server-written field on
    // every sibling entry survives it.
    txn.update(ref, {
      videoLinks: preserveMediaTransform(rawVideoLinks, links, next),
      updatedAt: serverTimestamp(),
    });
  });
}

/**
 * TAKE ENTRIES OFF A CLASS'S MUSIC & VIDEOS, the one door every Remove
 * button now goes through.
 *
 * VIDEO goes to the server (offeringWrite's removeMediaLink), because
 * removing a mirrored board clip has to take that clip off its class post in
 * the same transaction (Bobbie's mirror ruling 2, 2026-09-09: "Remove both
 * ways") and `classPosts.attachments` is server-owned - the rules deny every
 * client write to it, so a browser physically cannot do the other half. The
 * words of the post stay, exactly as a taken-back post's words do.
 *
 * MUSIC is unchanged and still goes through transformOfferingMediaLinks, so
 * the R49 ledger keeps owning it. Music is packet M2.
 *
 * Removal is BY ID either way: a teammate's upload landing mid-tap is never
 * erased along with the entry being removed, and removing an id that is
 * already gone succeeds having removed nothing.
 *
 * @returns how many entries actually left the list
 */
export async function removeOfferingMediaLinks(
  offeringId: string,
  kind: 'music' | 'video',
  linkIds: readonly string[]
): Promise<number> {
  const ids = [...new Set(linkIds.filter((value) => typeof value === 'string' && value))];
  if (!ids.length) return 0;
  if (kind === 'music') {
    const removing = new Set(ids);
    let removed = 0;
    await transformOfferingMediaLinks(offeringId, 'music', (fresh) => {
      const kept = fresh.filter((link) => !removing.has(link.id));
      // SET, never incremented, so a transaction retry cannot double-count.
      removed = fresh.length - kept.length;
      return kept;
    });
    return removed;
  }
  const user = getAuth().currentUser;
  if (!user) throw new Error('Sign in required.');
  const result = await removeOfferingMediaLinkViaServer(user, offeringId, ids);
  return result.removed ?? 0;
}

/**
 * RELEASE A SPENT UPLOAD'S UPLOADING ROW, AND ONLY WHILE IT IS STILL ONE.
 *
 * The legacy class editor (Offering.tsx) reserves an Uploading row, then
 * finalize REPLACES it with the finished video under the SAME id. When the
 * server answers attachment_already_used, the upload behind that row is spent
 * and the row can never be finished, so it should go. But an error is not
 * proof of what the id holds now: if an earlier finalize of that same id
 * landed with its answer lost, the id is the finished video, and removing it
 * deletes a real video (refute round one on #1215, P2-1, then the final
 * review). So the class is read from the SERVER, never the cache and never
 * the screen's own copy, whose stale "still Uploading" is exactly the case
 * that would delete a finished video, and the row is removed only when it is
 * an Uploading placeholder right now (isMediaUploadReservation, the same test
 * the editor uses to draw that row). A finished row, a missing row, or a read
 * that fails all remove nothing.
 *
 * @returns true when the Uploading row was there and is now removed
 */
export async function releaseSpentVideoUploadPlaceholder(
  offeringId: string,
  reservationId: string,
  deps: {
    readVideoLinks?: (offeringId: string) => Promise<readonly unknown[]>;
    removeVideoLinks?: (offeringId: string, linkIds: readonly string[]) => Promise<unknown>;
  } = {}
): Promise<boolean> {
  const readVideoLinks = deps.readVideoLinks ?? (async (id: string) => {
    if (!db) throw new Error('Firestore not initialized.');
    const snap = await getDocFromServer(doc(db, 'offerings', id));
    const videoLinks = (snap.data() as { videoLinks?: unknown } | undefined)?.videoLinks;
    return Array.isArray(videoLinks) ? videoLinks : [];
  });
  const removeVideoLinks = deps.removeVideoLinks
    ?? ((id: string, linkIds: readonly string[]) => removeOfferingMediaLinks(id, 'video', linkIds));
  let links: MediaLink[];
  try {
    links = sortMediaNewestFirst(await readVideoLinks(offeringId));
  } catch {
    return false;
  }
  const row = links.find((link) => link.id === reservationId);
  if (!row || !isMediaUploadReservation(row)) return false;
  await removeVideoLinks(offeringId, [reservationId]);
  return true;
}

/** Persist a final-cut toggle, transactionally (see transformOfferingMediaLinks). */
export async function setMusicLinkFinalCut(
  offering: Pick<OfferingDoc, 'id' | 'musicLinks'>,
  linkId: string,
  finalCut: boolean
): Promise<void> {
  await toggleMediaLinkFinalCut(offering.id, 'music', linkId, finalCut);
}

/**
 * Flip one media link's final-cut flag on EITHER list, then tell the Directors
 * when a piece of music has just become a final cut.
 *
 * Kept separate from setMusicLinkFinalCut above, which is hardcoded to
 * musicLinks: calling that one from a video tile would silently rewrite the
 * wrong array. The alert is music-only on purpose. Bobbie asked for "all Final
 * Cut music uploads", and a final cut of a VIDEO is a different thing that
 * nobody asked to be told about.
 */
export async function toggleMediaLinkFinalCut(
  offeringId: string,
  kind: 'music' | 'video',
  linkId: string,
  finalCut: boolean
): Promise<void> {
  if (kind === 'music') {
    await sendMusicRequest({ action: 'setMusicFinalCut', offeringId, linkId, finalCut });
  } else {
    await transformOfferingMediaLinks(offeringId, kind, (links) => withMusicLinkFinalCut(links, linkId, finalCut));
  }
  if (kind === 'music' && finalCut) {
    await notifyProductionUpdate(offeringId, 'final-cut', linkId);
  }
}

/**
 * Persist an edited label on EITHER list, transactionally (see
 * transformOfferingMediaLinks). Covers music and video the same way
 * toggleMediaLinkFinalCut does, with its own `kind` argument rather than a
 * musicLinks-only helper like setMusicLinkFinalCut.
 *
 * Deliberately does NOT announce anything and does NOT call
 * notifyProductionUpdate: a relabel is a typo fix, not news, unlike a fresh
 * final-cut mark. See MediaPanel.tsx's own announceNewMedia, which only ever
 * runs from the add paths.
 */
export async function relabelMediaLink(
  offeringId: string,
  kind: 'music' | 'video',
  linkId: string,
  newLabel: string
): Promise<void> {
  await transformOfferingMediaLinks(offeringId, kind, (links) =>
    withMediaLinkLabel(links, linkId, newLabel)
  );
}

function slugForDanceTrack(label: string, index: number): string {
  const slug = label
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
  return slug || `dance-${index + 1}`;
}

export function makeDanceTrack(label: string, index = 0): DanceTrack {
  const trimmed = label.trim();
  return { id: slugForDanceTrack(trimmed, index), label: trimmed };
}

function normalizeDanceTrack(track: unknown, index: number): DanceTrack | null {
  if (!track || typeof track !== 'object') return null;
  const candidate = track as Partial<DanceTrack>;
  const label = typeof candidate.label === 'string' ? candidate.label.trim() : '';
  if (!label) return null;
  const id = typeof candidate.id === 'string' && candidate.id.trim()
    ? candidate.id.trim()
    : slugForDanceTrack(label, index);
  return { id, label };
}

export function normalizeDanceTracks(tracks: readonly unknown[] | null | undefined): DanceTrack[] {
  if (!Array.isArray(tracks)) return [];
  const seen = new Set<string>();
  const normalized: DanceTrack[] = [];
  for (let index = 0; index < tracks.length; index += 1) {
    const track = normalizeDanceTrack(tracks[index], index);
    if (!track || seen.has(track.id)) continue;
    seen.add(track.id);
    normalized.push(track);
  }
  return normalized.slice(0, 8);
}

export function defaultDanceTracksForStyle(style: string | null | undefined): DanceTrack[] {
  if (style === 'Combo Tap/Ballet') return ['Tap', 'Ballet'].map(makeDanceTrack);
  if (style === 'Combo Contemporary/Commercial') return ['Contemporary', 'Commercial'].map(makeDanceTrack);
  if (style === 'Combo Comp/Commercial') return ['Competition', 'Commercial'].map(makeDanceTrack);
  return [];
}

export function danceTracksForOffering(offering: Pick<OfferingDoc, 'style' | 'danceTracks'>): DanceTrack[] {
  const explicit = normalizeDanceTracks(offering.danceTracks);
  return explicit.length > 0 ? explicit : defaultDanceTracksForStyle(offering.style);
}

/**
 * Names of the classes a dancer is on, from their own StudentDoc.memberships
 * ids resolved against a live offerings map. Manage Users' dancer panel
 * (ManageUsers.tsx, DancersForFamily) uses this to answer Bobbie's
 * 2026-08-19 ask - "click and see the classes that are assigned to that
 * dancer." Same resolve-then-filter SHAPE as Students.tsx's StudentRow uses
 * for its own inline enrolledOfferings - that inline version still exists
 * and is not replaced by this (it needs the actual OfferingDoc objects, not
 * just names, for its own ids-as-keys rendering); this is a fresh,
 * independently testable extraction for Manage Users, not a swap-in
 * replacement of StudentRow's copy. An id whose offering has not loaded yet
 * or was deleted is silently dropped: this is a short informational list,
 * not a place that owes an honest missing/loading distinction the way a
 * roster count does (see honestLabel.ts for where that distinction does
 * matter) - the CALLER is responsible for not rendering this during an
 * offerings load window, see DancerClassesToggle's own loading guard.
 */
export function classNamesForMembershipIds(
  membershipIds: readonly string[] | undefined | null,
  offeringsById: ReadonlyMap<string, Pick<OfferingDoc, 'name'>>
): string[] {
  const names: string[] = [];
  for (const id of membershipIds ?? []) {
    const offering = offeringsById.get(id);
    if (offering) names.push(offering.name);
  }
  return names;
}

export function danceTrackLabelForId(
  tracks: readonly DanceTrack[],
  trackId: string | null | undefined,
  fallback?: string | null
): string | null {
  const fallbackLabel = typeof fallback === 'string' ? fallback.trim() : '';
  if (!trackId) return fallbackLabel || null;
  return tracks.find((track) => track.id === trackId)?.label ?? (fallbackLabel || trackId);
}


// 6 GB, matching the gateway's own ATTACHMENT_VIDEO_MAX_BYTES ceiling
// (attachmentUpload.mjs has the sizing math). This guards uploadClassVideo
// below, a local, fast pre-check before the upload starts - the real
// ceiling is attachmentUpload.mjs's own check at reserve time, since
// security-audit-2026-09-16 finding H-3 moved this lane off a direct
// Firebase Storage write and onto the same 'staffClassMedia' gateway lane
// classFamilyMedia and dancerClassMedia already used (App Check, the shared
// hourly/daily quota, and a magic-byte check at finalize, none of which the
// old direct-Storage write had). shared/storage.rules' `class-media` path
// is legacy read-only now; nothing writes through it any more.
//
// uploadFamilyClassVideo, a direct-Firebase-Storage write with no caller
// anywhere in hub/src, was removed outright (round 2 refute, P3 4) rather
// than kept correct for a wiring-back-up that never happened; the door it
// wrote through, shared/storage.rules' family-class-videos, is closed for
// good on this same audit's round 2 send-back (P2-B).
//
// The PARENT video upload added 2026-08-18 (MediaPanel.tsx's uploadFamilyVideo,
// target kind 'classFamilyMedia') does NOT use this constant - it goes
// through uploadAttachmentViaGateway, which bypasses Storage rules entirely,
// so it uses CLASS_FAMILY_VIDEO_MAX_BYTES below instead, which already
// matched the gateway's own ATTACHMENT_VIDEO_MAX_BYTES before this change.
export const CLASS_VIDEO_MAX_BYTES = 6 * 1024 * 1024 * 1024;

// 6 GB, matches the gateway's ATTACHMENT_VIDEO_MAX_BYTES (see
// attachmentUpload.mjs for the sizing math). Used ONLY by MediaPanel.tsx's
// parent-facing uploadFamilyVideo, the one video-upload lane on the class
// page that goes through the gateway instead of a direct Storage SDK write.
export const CLASS_FAMILY_VIDEO_MAX_BYTES = 6 * 1024 * 1024 * 1024;

function safeStorageName(name: string): string {
  return (name || 'upload').replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 120);
}

/**
 * Shrink a photo in the browser before it uploads.
 *
 * Card art renders at a few hundred pixels; a full-resolution phone shot is
 * several thousand pixels and can be tens of megabytes. Downscaling to a
 * 2000px JPEG at 0.85 quality brings any photo down to roughly 0.5-2MB:
 * comfortably under the 8 MB cap, and much faster to upload on cellular. The
 * same pass converts iPhone HEIC to JPEG so a photo displays on every device.
 *
 * The pipeline itself lives in lib/photoShrink.ts (since 2026-10-19): a
 * Worker first, the main thread with its <img> fallback second, one photo at
 * a time page wide (photoShrinkTurn.ts), and a hard deadline with a decided
 * fallback so a decode that never answers can no longer hold a send forever.
 * It rejects with a PhotoShrinkError whose message is written for the person
 * when a photo cannot be sent.
 *
 * `sourceType` lets a caller that infers MIME from the file extension (the
 * blank-File.type HEIC disease, see lib/threads.ts) tell this pipeline what
 * the file really is; it defaults to the browser-reported file.type.
 */
export async function downscaleImage(file: File, sourceType?: string): Promise<Blob> {
  // The upload record (speed plan P1) reads what happened here from
  // photoShrinkOutcome.ts, so no caller has to carry it to the gateway.
  // shrinkPhotoForUpload (photoShrink.ts) takes the one-at-a-time turn and
  // holds the deadline; a photo it refuses throws, so it gets no note.
  const startedAt = Date.now();
  const blob = await shrinkPhotoForUpload(file, sourceType, browserPhotoShrinkDeps);
  // Read from the pipeline, not from blob !== file: an original JPEG that
  // had its GPS taken out is a new blob but was not shrunk.
  const result = photoShrinkResultOf(blob);
  notePhotoShrink(blob, result === 'timeout' ? 'timeout' : result === 'ok', Date.now() - startedAt);
  return blob;
}

/** Reads a Firebase error's `.code` safely, without assuming the error shape. */
function firebaseErrorCode(e: unknown): string | null {
  if (!e || typeof e !== 'object') return null;
  const code = (e as { code?: unknown }).code;
  return typeof code === 'string' && code ? code : null;
}

// The upload timeout race moved into storageUpload.ts, because when the
// timeout wins, the abandoned (possibly never-settling) upload's in-flight
// counter claim has to be released or the version auto-update stays blocked
// for the life of the tab - and only storageUpload owns that counter.
// Re-exported here so the existing call sites and their imports keep working.
export { PHOTO_UPLOAD_TIMEOUT_MS, withUploadTimeout as withPhotoUploadTimeout } from './storageUpload';

async function uploadClassImage(
  prefix: 'class-photos' | 'class-avatar-photos' | 'class-post-images',
  offeringId: string,
  uid: string,
  file: File
): Promise<string> {
  if (!storage) throw new Error('Firebase Storage not initialized.');
  if (!offeringId) throw new Error('Class is required.');
  if (!uid) throw new Error('Sign in required.');
  if (!file.type.startsWith('image/')) {
    throw new Error('Choose an image file.');
  }
  const body = await downscaleImage(file);
  if (body.size >= CLASS_PHOTO_MAX_BYTES) {
    throw new Error('That photo is too large even after shrinking. Pick one under 8 MB.');
  }
  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const safeName = safeStorageName(file.name || 'class-photo');
  const ref = storageRef(storage, `${prefix}/${offeringId}/${uid}/${id}-${safeName}`);
  const contentType = body === file ? file.type : 'image/jpeg';
  try {
    await uploadBytes(ref, body, { contentType });
  } catch (e) {
    // A refused upload covers several very different problems and the raw
    // Firebase message alone doesn't say which: the rules genuinely refused
    // this person, the app is pointed at a bucket the rules were never
    // deployed to (every upload fails for everybody), or the network gave up
    // partway through. /hub-check has a probe that tells them apart, so send
    // people there instead of leaving them staring at an opaque failure - but
    // the exact code still has to survive into whatever screenshot comes
    // back, since storage/unauthorized points at rules or App Check while
    // storage/retry-limit-exceeded points at the network instead, and those
    // get fixed in different places.
    const code = firebaseErrorCode(e);
    if (code) {
      throw new Error(
        `Firebase Storage refused the photo. Open /hub-check and run the Storage probe while signed in: it will say whether this is your account, the rules, or App Check enforcement blocking Storage for everybody. (${code})`
      );
    }
    throw e;
  }
  // The read-back is guarded like the upload, and for the same reason it is
  // OUTSIDE the try above: a failure here means the bytes are already in the
  // bucket but the caller cannot mint the download URL, which is a different
  // problem with a different fix than a refused write. Bobbie hit exactly this
  // on class-post-images, twice, and got the raw Firebase string with a full
  // storage path in it.
  try {
    return await getDownloadURL(ref);
  } catch (e) {
    const code = firebaseErrorCode(e);
    if (code) {
      throw new Error(
        `The photo uploaded, but reading it back was refused, so it cannot be attached. Open /hub-check and run the Storage probe while signed in; this is a rules problem on the read side, not your upload. (${code})`
      );
    }
    throw e;
  }
}

/** Class-card identity photo (broadly visible catalog art). */
export async function uploadClassPhoto(offeringId: string, uid: string, file: File): Promise<string> {
  return uploadClassImage('class-photos', offeringId, uid, file);
}

/**
 * The class page's own avatar-tile photo. A SEPARATE upload from
 * uploadClassPhoto, on its own storage prefix - see OfferingDoc.avatarPhotoUrl's
 * comment for why the tile needed its own slot.
 */
export async function uploadClassAvatarPhoto(offeringId: string, uid: string, file: File): Promise<string> {
  return uploadClassImage('class-avatar-photos', offeringId, uid, file);
}

/**
 * Photo attached to a class-feed post. Stored on a staff-only-read path so it
 * cannot be enumerated by other families; recipient families view it through
 * the tokenized download URL saved on the recipient-scoped classPosts doc.
 */
export async function uploadClassPostImage(offeringId: string, uid: string, file: File): Promise<string> {
  return uploadClassImage('class-post-images', offeringId, uid, file);
}

export async function uploadClassVideo(
  offeringId: string,
  user: User,
  file: File,
  danceTrack?: DanceTrack | null,
  extra?: { category?: MediaCategory; addedByName?: string },
  /** 0..1 upload progress (task #88 - a 500MB video takes minutes, and a
   * frozen-looking "Sending the video to the stage..." with no number
   * moving reads as broken, not slow). Optional; callers that don't want a
   * live percent can omit it exactly as before this parameter existed.
   * Its onConfirmed, if it has one, rides through to the gateway for the
   * bar's speed and time left (UploadProgressListener in uploadWait.ts). */
  onProgress?: UploadProgressListener,
  /** Aborting this cancels the byte transfer, rejecting with code
   * 'storage/canceled' - same contract the old direct-Storage lane gave
   * callers, so MediaPanel.tsx's and Offering.tsx's existing Cancel
   * handling needs no change. Without it a Cancel button can only hide the
   * form while the full video keeps uploading on the person's data plan. */
  signal?: AbortSignal,
  uploadId?: string
): Promise<MediaLink & { attachmentReservationId: string }> {
  if (!offeringId) throw new Error('Class is required.');
  if (!user?.uid) throw new Error('Sign in required.');
  if (!file.type.startsWith('video/')) {
    throw new Error('Choose a video file.');
  }
  if (file.size >= CLASS_VIDEO_MAX_BYTES) {
    throw new Error('Videos need to be under 6 GB. If it is larger, upload it to Drive or YouTube and paste the link.');
  }
  const id = uploadId || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  // THE BYTES (security-audit-2026-09-16 finding H-3). Reserve, send and
  // finalize through attachmentUpload.mjs's 'staffClassMedia' gateway lane -
  // the same App Check requirement, the same shared hourly/daily quota, and
  // the same magic-byte check at finalize that classFamilyMedia and
  // dancerClassMedia already had. `id` doubles as the gateway's requestId
  // seed (used only on a first attempt; a retry of the same picked file
  // reuses whatever attachmentUploadResume.ts already persisted for it) so
  // the eventual videoLinks entry id and the gateway reservation's own
  // idempotency key stay one identity, not two.
  const [{ AttachmentGatewayError }, { uploadAttachmentViaGatewayResumable }] =
    await loadClassVideoUploadCode();
  let uploaded;
  try {
    uploaded = await uploadAttachmentViaGatewayResumable({
      target: { kind: 'staffClassMedia', offeringId },
      file: { name: file.name, size: file.size, lastModified: file.lastModified },
      source: { blob: file, name: file.name, contentType: file.type },
      user,
      mintRequestId: () => id,
      onProgress,
      signal,
    });
  } catch (error) {
    if (error instanceof AttachmentGatewayError) {
      if (error.code === 'upload_canceled') {
        throw Object.assign(new Error('Upload canceled.'), { code: 'storage/canceled' });
      }
      // A definite, server-decided or protocol-decided outcome (quota,
      // magic bytes, a dead reservation) - the message is already honest
      // and actionable, never "may have finished".
      throw error;
    }
    // A raw network failure with no clean response at all does not prove
    // the object is absent - the final chunk, or the finalize call itself,
    // may have committed before its acknowledgement was lost. Same
    // "committed but unconfirmed" contract the old direct-Storage lane gave
    // callers, so Offering.tsx's existing recovery buttons still apply.
    //
    // No recoverUpload read-back is attached on purpose. The old lane could
    // re-read a deterministic Storage path; the gateway picks the path
    // server-side, and re-entering the gateway here could send bytes again.
    // managedClassVideoUpload.ts reads a missing recoverUpload as "keep the
    // failure visible, offer no Retry", so the background job never resends
    // a video that may already have landed. A transfer that DID return
    // still gets the save-only Retry there, because the class write is the
    // manager's separate commit step.
    throw uploadOutcomeUnknownError(error);
  }
  const addedByName = extra?.addedByName?.trim();
  return {
    id,
    url: uploaded.url,
    label: file.name.replace(/\.[^.]+$/, '').trim() || 'Uploaded video',
    addedAt: Date.now(),
    // Staff-only upload path; see the addedByRole doc comment on MediaLink.
    addedByRole: 'staff',
    ...(extra?.category ? { category: extra.category } : {}),
    ...(addedByName ? { addedByName } : {}),
    ...(danceTrack ? { danceTrackId: danceTrack.id, danceTrackLabel: danceTrack.label } : {}),
    storagePath: uploaded.storagePath,
    contentType: uploaded.contentType,
    fileName: file.name,
    // Transient: proves these bytes to writeStaffVideoLink's consume step.
    // Never itself written to videoLinks - offeringWrite.mjs strips it.
    attachmentReservationId: uploaded.reservationId,
  };
}

/**
 * Resolve a stalled reservation from its deterministic Storage path. A null
 * result means the resumable upload has not produced a completed object yet,
 * so the reservation stays protected. A completed object becomes a normal
 * MediaLink on any signed-in device, without relying on the uploader's local
 * browser state.
 *
 * H-3 (security-audit-2026-09-16) moved the staff video lane onto the
 * gateway, which decides the storage path server-side at reserve time - a
 * NEW pending row therefore carries no storagePath at all (see
 * uploadClassVideo's call site in Offering.tsx), and this always throws for
 * one, honestly, rather than probing a path that was never right. This is
 * not only an old row: it can be the upload from a minute ago. Cross-device
 * recovery by reservation id is a real fix, not yet built (see the queue).
 */
export async function recoverUploadedMediaLink(reservation: MediaLink): Promise<MediaLink | null> {
  if (!reservation.storagePath) {
    throw new Error('This upload cannot be checked from a different device yet. Go back to the device it started on, or upload it again.');
  }
  if (!storage) {
    throw new Error('Storage is unavailable right now. The upload reservation was left protected.');
  }
  try {
    const url = await getDownloadURL(storageRef(storage, reservation.storagePath));
    return {
      ...reservation,
      url,
      label: reservation.label.replace(/^Uploading\s+/i, '').trim() || reservation.fileName || 'Uploaded media',
    };
  } catch (error) {
    // A resumable upload is not visible until it completes. Object-not-found
    // therefore means "not complete yet," never "safe to delete."
    if ((error as { code?: string })?.code === 'storage/object-not-found') return null;
    throw error;
  }
}
/**
 * The four age cards on the public website, and the only values `ageBands`
 * may hold. Order is the order a parent reads them in.
 */
export const AGE_BANDS = ['mini', 'jr', 'teen', 'adult'] as const;
export type AgeBand = (typeof AGE_BANDS)[number];

export const AGE_BAND_LABELS: Record<AgeBand, string> = {
  mini: 'Minis',
  jr: 'Juniors',
  teen: 'Teens',
  adult: 'Adults',
};

/**
 * Keep only known bands, in the site's own order, with no duplicates.
 *
 * Anything unrecognised is dropped rather than passed along: this value
 * decides which age card a class appears under on a page strangers read, so a
 * typo should mean "no card" rather than "some card nobody intended".
 */
export function normalizeAgeBands(raw: unknown): AgeBand[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set(raw.filter((v): v is string => typeof v === 'string'));
  return AGE_BANDS.filter((band) => seen.has(band));
}

type OfferingLabelInput = Pick<OfferingDoc, 'name' | 'style' | 'kind'>;

function explicitOfferingKind(offering: Pick<OfferingDoc, 'kind'>): OfferingKind | null {
  return offering.kind === 'class'
    || offering.kind === 'comp-class'
    || offering.kind === 'comp-dance'
    || offering.kind === 'workshop'
    ? offering.kind
    : null;
}

export function isCompClassOffering(offering: Pick<OfferingDoc, 'kind'>): boolean {
  return explicitOfferingKind(offering) === 'comp-class';
}

/**
 * Does this offering carry the EXPLICIT kind that both the family video
 * Storage rule and the family classPosts create rule check -
 * shared/storage.rules' family-class-videos rule and shared/firestore.rules'
 * isFamilyCompBoardCreate() both read offerings/{id}.kind with a 'class'
 * default and require 'comp-class' or 'comp-dance', with NO name/style
 * heuristic. isCompetitionOffering below falls back to one for a doc with no
 * kind at all, and that heuristic used to gate the family board composer too
 * - so an offering NAMED like a comp team but missing an explicit kind (an
 * imported doc, comp2026o-* ids among them) showed a working-looking
 * composer whose every send the rules actually denied: a video hit
 * storage/unauthorized, a text-only post hit a Firestore permission denial.
 * Live bug, 2026-08-08.
 *
 * Use this to gate the family board composer (post or video) specifically.
 * isCompetitionOffering's heuristic stays correct and unchanged for every
 * label, filter and other surface that already reads it - including the
 * "Message Kim" vs "Message us" label right next to this same composer,
 * which is deliberately NOT switched to this stricter check.
 */
export function isExplicitCompOffering(offering: Pick<OfferingDoc, 'kind'>): boolean {
  const kind = explicitOfferingKind(offering);
  return kind === 'comp-class' || kind === 'comp-dance';
}

export function isCompetitionOffering(offering: OfferingLabelInput): boolean {
  const kind = explicitOfferingKind(offering);
  if (kind === 'comp-dance' || kind === 'comp-class') return true;
  if (kind === 'class' || kind === 'workshop') return false;
  const name = offering.name.toLowerCase();
  return (
    offering.style === 'Combo Contemporary/Commercial' ||
    offering.style === 'Combo Comp/Commercial' ||
    name.includes('comp') ||
    name.includes('competition') ||
    name.includes('company') ||
    name.includes('team') ||
    name.includes('solo') ||
    name.includes('duet') ||
    name.includes('trio')
  );
}

/**
 * Does this offering have a REC identity - a weekly class slot that performs
 * in the recital? Bobbie's ruling (2026-08-05): kind 'comp-class' is BOTH a
 * comp dance AND a rec class (weekly slot, performs in the recital,
 * competes); 'comp-dance' is comp only; 'class' is rec only; 'workshop' is
 * neither. A comp-class belongs on every rec surface (the recital builder's
 * default selection, the family Rec filter, the production grid's Rec
 * filter) exactly as if it were a plain class, in ADDITION to belonging on
 * every comp surface via isCompetitionOffering - it is not a choice between
 * the two.
 *
 * Workshops stay false here even though they are neither comp nor rec,
 * pending Bobbie's call on where they belong (audit Ambiguous Q6). Until she
 * rules, they stay off every rec surface, the same as before this function
 * existed.
 *
 * Legacy fallback for an untagged offering (no kind at all): the negation of
 * isCompetitionOffering, which matches what every rec surface already did
 * before this function existed.
 */
export function hasRecIdentity(offering: OfferingLabelInput): boolean {
  const kind = explicitOfferingKind(offering);
  if (kind === 'class' || kind === 'comp-class') return true;
  if (kind === 'comp-dance' || kind === 'workshop') return false;
  return !isCompetitionOffering(offering);
}

/**
 * The class picker's view of an offering (components/ClassPicker.tsx, shared
 * by Place on /registrations and Add to a class on the dancer page): comp
 * draws the Comp pill and the comp-dance heads-up, preselectable mirrors
 * offeringEligibleForAutoEnroll on the server (a comp-class may pre-tick, a
 * comp dance or workshop never). ONE mapping so the two pages cannot drift.
 */
export function toGuessOffering(offering: OfferingDoc): GuessOffering {
  return {
    id: offering.id,
    name: offering.name,
    studioProName: offering.studioProName,
    comp: isCompetitionOffering(offering),
    preselectable: hasRecIdentity(offering),
  };
}

/**
 * The edge-bar color for a class list row or card, per family feedback
 * yZaESQ3p: "Can you make these different colors for comp and rec? IE rec
 * classes with a black bar and Comp with a green bar? ... so they're
 * distinguishable at a quick glance". One function so every class list
 * surface (the family classes list, a dancer's schedule rows, the staff
 * Offerings cards) draws the same two colors from the same comp-ness call
 * - isCompetitionOffering, never a hardcoded name check of its own - so the
 * bar and the label it sits beside can never disagree.
 *
 * Comp gets the brand's own green (studio-green); rec gets ink, plain and
 * quiet on purpose - the ask was for the two to read apart from across a
 * room, not for rec to compete with the primary action color.
 */
export function compRecEdgeColor(isComp: boolean): string {
  return isComp ? 'var(--studio-green)' : 'var(--ink)';
}

/**
 * Technique classes are training only - they never perform. Bobbie:
 * "anything that has technique in the name doesn't perform". They stay real
 * classes (kids attend them, they sit on the schedule), so we do NOT delete or
 * re-kind them; we just hide them from every PERFORMANCE surface: the comp
 * dance dashboard, the recital lineup, music tracking, costume tracking, the
 * comp look, and the comp-day sync. A technique class needs no music and no
 * costume because it never takes a stage.
 *
 * Name-based on purpose: it matches how the studio names these and needs no
 * per-doc migration or credentials. It catches technique classes of EVERY kind
 * - the comp-class ones ("Emerald + Gold Technique", "Sapphire/Onyx Technique",
 * "Diamond + Platinum Technique") and the rec ones ("Jr Technique", "Boys
 * Technique") - which is correct: none of them performs. No real competition
 * routine in the roster carries the word, so the filter cannot hide a
 * performing dance ("Ballet Tech/Prepointe" contains "Tech", not "technique",
 * and is not matched).
 */
export function isTechniqueOnly(offering: Pick<OfferingDoc, 'name'>): boolean {
  return (offering.name ?? '').toLowerCase().includes('technique');
}

/**
 * Does this offering ever appear in a performance context (comp dance
 * dashboard, recital, music, costume, comp-day)? Everything performs except a
 * technique class. Use this to keep technique classes out of those surfaces
 * while leaving them fully present for attendance and scheduling.
 */
export function performsInShows(offering: Pick<OfferingDoc, 'name'>): boolean {
  return !isTechniqueOnly(offering);
}

export function offeringKindLabel(offering: OfferingLabelInput): string {
  const kind = explicitOfferingKind(offering);
  if (kind === 'comp-class') return 'Comp class';
  if (kind === 'comp-dance') return 'Comp dance';
  if (kind === 'workshop') return 'Workshop';
  if (kind === 'class') return 'Rec class';
  return isCompetitionOffering(offering) ? 'Comp dance' : 'Rec class';
}
// offeringNoun/offeringNounTitle/offeringBoardLabel/offeringPageLabel below
// each end in the same legacy-name fallback as offeringKindLabel above (audit
// 2026-08-05): a doc with no `kind` at all still resolves through
// isCompetitionOffering's name heuristic instead of defaulting to the rec
// label, so an untagged legacy comp offering reads "comp" consistently
// everywhere instead of "comp" on the pill and "rec" on the noun.

export function offeringNoun(offering: OfferingLabelInput): string {
  const kind = explicitOfferingKind(offering);
  if (kind === 'comp-class') return 'comp class';
  if (kind === 'comp-dance') return 'comp dance';
  if (kind === 'workshop') return 'workshop';
  if (kind === 'class') return 'rec class';
  return isCompetitionOffering(offering) ? 'comp dance' : 'rec class';
}

export function offeringNounTitle(offering: OfferingLabelInput): string {
  const kind = explicitOfferingKind(offering);
  if (kind === 'comp-class') return 'Comp class';
  if (kind === 'comp-dance') return 'Comp dance';
  if (kind === 'workshop') return 'Workshop';
  if (kind === 'class') return 'Rec class';
  return isCompetitionOffering(offering) ? 'Comp dance' : 'Rec class';
}

export function offeringBoardLabel(offering: OfferingLabelInput): string {
  const kind = explicitOfferingKind(offering);
  if (kind === 'comp-class') return 'Comp class board';
  if (kind === 'comp-dance') return 'Comp dance board';
  if (kind === 'workshop') return 'Workshop board';
  if (kind === 'class') return 'Class board';
  return isCompetitionOffering(offering) ? 'Comp dance board' : 'Class board';
}

export function offeringPageLabel(offering: OfferingLabelInput): string {
  const kind = explicitOfferingKind(offering);
  if (kind === 'comp-class') return 'Comp class page';
  if (kind === 'comp-dance') return 'Comp dance page';
  if (kind === 'workshop') return 'Workshop page';
  if (kind === 'class') return 'Class page';
  return isCompetitionOffering(offering) ? 'Comp dance page' : 'Class page';
}

export function offeringFeedHref(offeringId: string, postId?: string, replyId?: string): string {
  // THE CUTOVER LINE. Bobbie, three separate times tonight, most recently
  // "/teach/classes still take you to the old class board instead of the new
  // class page": every feed link in the app now lands on the one-page class
  // view. The old /feed route stays alive for notification links written
  // before tonight; both pages read the same #class-post-{id} anchor, so old
  // deep links still land on their post. Reverting the cutover is reverting
  // this one function.
  const base = `/class/${encodeURIComponent(offeringId)}`;
  if (!postId) return base;
  // ?openReply=<replyId> BEFORE the hash (a query string cannot follow a
  // fragment): ClassPage reads it to force that post's reply thread open on
  // arrival, so a reply notification lands somewhere the reply is actually
  // visible instead of behind a collapsed toggle (Bobbie, 2026-08-26).
  const query = replyId ? `?openReply=${encodeURIComponent(replyId)}` : '';
  return `${base}${query}#class-post-${encodeURIComponent(postId)}`;
}

export function offeringMessageHref(offeringId: string): string {
  return `/messages/new?mode=classBoard&offeringId=${encodeURIComponent(offeringId)}`;
}

export interface OfferingDoc extends OfferingTeacherFields {
  id: string;
  /** Server-owned link back to the signup event for workshop classes. */
  signupEventId?: string;
  name: string;
  kind?: OfferingKind | null;
  style: DanceStyle;
  level: Level;
  teacherUid: string | null;
  /** Executive co-teachers. teacherUid remains the lead and legacy authority. */
  coTeacherUids?: string[];
  /**
   * Denormalized display name for teacherUid, written by the Director class
   * form. Families cannot read staff user docs, so this is the only way the
   * family class page can say who teaches the class. May be missing on
   * older docs until the class is re-saved.
   */
  teacherName?: string | null;
  /** Family-safe names keyed by every currently linked teacher uid. */
  teacherNames?: Record<string, string>;
  /**
   * Temporary teaching seats and the claims that currently justify them,
   * written only by the sub claim lane (netlify/functions/lib/subClassGrant.mjs).
   * A uid here is a teacher of the class for the sub window (ACCESS) but not a
   * weekly teacher of it (SCHEDULE): see lib/subSeatSchedule.ts. Carried by
   * normalizeOfferingDoc below, so every screen reading useOfferings() can
   * tell the two apart.
   */
  subSeats?: Record<string, { byRequestIds?: string[] }>;
  /**
   * Jr assistants attached to this class. Drives assistant attendance: without
   * it there was no record that a Jr assistant belonged to a class at all, so
   * there was nothing to take attendance against. Absent on classes written
   * before this existed; read sites treat missing as none.
   */
  assistantUids?: string[];
  /**
   * Display name for each assistantUids entry, written by
   * setOfferingAssistant the same moment it assigns the uid. Exists because
   * a plain staff viewer's useUsersList() query is scoped to STAFF_ROLES and
   * Firestore rules refuse an ensemble-role viewer a family-role user doc
   * outright (parent-contact privacy, shared/firestore.rules ~line 1174) -
   * a designated-dancer Jr assistant's account is unreadable to most staff
   * viewers by design, so their name has nowhere else to come from. Mirrors
   * teacherNames above. Absent on assistants assigned before this existed.
   */
  assistantNames?: Record<string, string>;
  studentIds: string[];
  /**
   * Dancers on this roster who are currently standing by. A SUBSET of
   * studentIds, never a replacement for it: an alternate is still on the
   * dance, still gets attendance taken, still gets a costume.
   */
  alternateStudentIds?: string[];
  /** Per-dancer reason and paper trail, keyed by studentId. */
  alternateInfo?: Record<string, AlternateInfo>;
  /**
   * Recital details - optional, blank until they get closer. The recital
   * number is the running order ("Dance #14"); recitalTime is free text
   * ("Saturday June 13, 2:00 PM show") since the recital schedule is loose
   * until the program is finalized. Families see these on the dancer profile.
   */
  recitalNumber?: string;
  recitalTime?: string;
  /**
   * Music + video links for this class - one per dance, so they're lists.
   * Manual for now (Director pastes Google Drive / YouTube / audio share
   * links) until the full Drive integration ships. Families see them on the
   * dancer profile with the newest upload highlighted.
  */
  musicLinks?: MediaLink[];
  musicReviewStatus?: MusicReviewStatus | null;
  videoLinks?: MediaLink[];
  /** Optional lanes for combo classes that rehearse two dances in one weekly class. */
  danceTracks?: DanceTrack[];
  /** Max spots in the class. Optional; drives the "spots open / Full" badge. */
  capacity?: number | null;
  /** Age group label, e.g. "3-5" preset or a custom "min-max" range string. */
  ageGroup?: string | null;
  /**
   * Which of the public website's age cards this class belongs on.
   *
   * Separate from `ageGroup` because most classes do not have one: 35 of the
   * studio's 66 carried no age at all, and the website was showing every one
   * of them under every age card. That put a pointe class on Minis, which is
   * not a neutral default - it is the site telling the parent of a four year
   * old that pointe suits their child (Bobbie, 2026-08-12, who also asked for
   * this field: "maybe we should add that as an option when setting up a
   * class... so it filters better").
   *
   * Empty means the website shows the class in the full list and in no age
   * filter, and says out loud how many classes that covers.
   */
  ageBands?: AgeBand[];
  /**
   * Who this class is for, e.g. "Approval required" or "Advanced/Competition" -
   * the qualifier the printed schedule carries next to a class name. Read-only
   * display for now; no edit surface writes it yet. Kept separate from
   * `familyNote` on purpose (Bobbie, 2026-08-18): a qualifier is class info,
   * not a message, so it must never surface as an inbox row the way a real
   * family-facing note does. Classes seeded before that ruling still hold
   * the label in `familyNote`; read it through whoItIsForLabel and
   * classNoteText (lib/classNoteInbox.ts, DECISIONS 159), never directly.
   */
  audienceLabel?: string | null;
  /** Family-facing note shown on dancer profiles for this class. */
  familyNote?: string | null;
  /**
   * WHEN THE NOTE ITSELF LAST MOVED, which is not the same question as
   * `updatedAt` (2026-08-18, the Slice D messages rebuild). Messages sorts
   * class-note cards by note age; reading the whole-doc `updatedAt` meant a
   * teacher fixing a recital time or a front-desk photo swap resurfaced a
   * three-week-old note at the top of every family's feed as if it were news.
   *
   * OPTIONAL: no class written before this pass carries it. Messages never
   * falls back to `updatedAt` for a class note (Kate's feedback DPe7WXUR,
   * DECISIONS 159: the video lane moves `updatedAt` in the same write that
   * posts the video, which put an old note beside every new post). A note
   * without it files by `createdAt` and shows no time; see
   * lib/classNoteInbox.ts. `shared/firestore.rules` pins it to the
   * server's own clock, the same way the Parent board pins `lastReplyAt`,
   * because a stamp that drives a sort must not be hand-pickable.
   */
  familyNoteUpdatedAt?: Timestamp | FieldValue;
  /** Photos, videos, and safe files shown with the family-facing note. */
  familyNoteAttachments?: MessageAttachment[];
  /** Optional visual identity for class and comp-dance cards/calendars. */
  themeColor?: string | null;
  photoUrl?: string | null;
  /**
   * Vertical framing of the cover photo, 0-100 (percent from the top).
   * A number so it can be range-checked before it reaches object-position;
   * the rules clamp it 0-100 and the reader clamps again on the way in.
   */
  coverFocusY?: number | null;
  /**
   * A SEPARATE photo for ClassAvatarTile (the small square tile on the
   * class page's own hero band), never the same field as photoUrl. That
   * tile deliberately shows initials rather than photoUrl - a single photo
   * appeared twice on the same screen otherwise - so a distinct photo here
   * is a genuinely different slot, not a rename or a reversal of that fix
   * (Bobbie, 2026-08-17, off a screenshot of the tile: "I want to be able
   * to add a photo here"). Uploaded via uploadClassAvatarPhoto, same shape
   * and size cap as the cover photo.
   */
  avatarPhotoUrl?: string | null;
  /** Comp day hair / makeup / lipstick / shoes / notes. Comp dances only. */
  compLook?: CompLook | null;
  /**
   * The song this comp dance is set to, e.g. "Sing Sing Sing" or "This Is Me".
   * Comp dance / comp class offerings only (the "which dances need music
   * decided" board reads `name` for the descriptive title regardless, and
   * leads with songName once it exists). Director-editable; see
   * nameSetByHuman below for why a boolean sibling exists for it too.
   */
  songName?: string | null;
  /**
   * True once a Director has explicitly saved `name` from the Hub UI. The
   * Fall-schedule and comp-dance seed scripts (hub/netlify/functions/
   * seedFallSchedule.mjs) re-run against the same printed source data all
   * season, and before this flag existed a re-run always put the printed
   * name back - so renaming a placeholder comp dance ("J/H/H" -> its real
   * name) silently reverted on the next seed pass. Seed scripts check this
   * before writing `name` and skip it when true. Absent/false means the
   * printed-schedule name is still authoritative.
   */
  nameSetByHuman?: boolean;
  /**
   * THE ZAPIER SEATBELT. The class name Studio Pro still knows this class by,
   * snapshotted automatically on the FIRST rename and never overwritten after
   * that (the rule pins both halves: write-once, and only ever to the stored
   * pre-rename name).
   *
   * Studio Pro auto-placement enrolls by EXACT Hub class name
   * (hub/netlify/functions/lib/zapAutoPlace.mjs, on Bobbie's own "The names of
   * dances should match exactly!"). Without this field, the moment a teacher
   * renamed their class every new Studio Pro student for it would silently
   * stop auto-enrolling and park on /registrations as undecidable, and nobody
   * would connect the two events for weeks. zapAutoPlace matches current name
   * OR this alias, so the rename is safe to hand to teachers.
   *
   * Null/absent means the class has never been renamed and its current name is
   * still the one Studio Pro uses.
   */
  studioProName?: string | null;
  /** Same "human wins over the seed script" flag as nameSetByHuman, for songName. */
  songNameSetByHuman?: boolean;
  /**
   * Which dance slot in the frozen Helper app this comp dance syncs into
   * (the Hub-to-Helper bridge, hub/netlify/functions/syncCompToHelper.mjs).
   * Director-only; set through the bridge's link mode. Null/absent means
   * not linked, and the sync refuses to run.
   */
  helperDanceId?: string | null;
  createdAt: Timestamp | FieldValue;
  updatedAt: Timestamp | FieldValue;
  createdBy: string;
}
function cleanString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function normalizeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return Array.from(
    new Set(
      value
        .filter((item): item is string => typeof item === 'string')
        .map((item) => item.trim())
        .filter(Boolean)
    )
  );
}

function normalizeStyle(value: unknown): DanceStyle {
  return cleanString(value) ?? 'Other';
}

function normalizeLevel(value: unknown): Level {
  return cleanString(value) ?? 'Level 1';
}

function normalizeMusicReviewStatus(value: unknown): MusicReviewStatus | null {
  return value === 'pending' || value === 'approved' || value === 'changes-needed'
    ? value
    : null;
}

/**
 * Firestore field paths are dot separated, so a studentId containing a dot,
 * a slash or a bracket would write to the wrong place (or throw) when used as
 * a key inside `alternateInfo.<id>`. Every id the Hub mints is a Firestore
 * auto-id, but a hand-seeded or imported one is not guaranteed to be, so both
 * the read and the write side refuse anything that is not plainly safe.
 */
function isSafeDocIdKey(value: string): boolean {
  return /^[A-Za-z0-9_-]{1,128}$/.test(value);
}

function normalizeAlternateInfoEntry(value: unknown): AlternateInfo | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const reason: AlternateReason | null =
    raw.reason === 'assigned' || raw.reason === 'attendance' ? raw.reason : null;
  if (!reason) return null;
  const causedByDates = Array.isArray(raw.causedByDates)
    ? raw.causedByDates.filter((d): d is string => typeof d === 'string').slice(0, 20)
    : undefined;
  const reinstateNote = cleanString(raw.reinstateNote);
  const reinstatedBy = cleanString(raw.reinstatedBy);
  return {
    reason,
    since: raw.since as Timestamp | FieldValue,
    setBy: cleanString(raw.setBy) ?? '',
    ...(causedByDates && causedByDates.length > 0 ? { causedByDates } : {}),
    ...(reinstateNote ? { reinstateNote } : {}),
    ...(reinstatedBy ? { reinstatedBy } : {}),
    ...(raw.reinstatedAt ? { reinstatedAt: raw.reinstatedAt as Timestamp | FieldValue } : {}),
  };
}

/** Reads the whole alternateInfo map, dropping entries it cannot make sense of. */
export function normalizeAlternateInfo(value: unknown): Record<string, AlternateInfo> {
  if (!value || typeof value !== 'object') return {};
  const out: Record<string, AlternateInfo> = {};
  for (const [studentId, entry] of Object.entries(value as Record<string, unknown>)) {
    if (!isSafeDocIdKey(studentId)) continue;
    const normalized = normalizeAlternateInfoEntry(entry);
    if (normalized) out[studentId] = normalized;
  }
  return out;
}

/** Reads a raw capacity value, keeping only whole numbers from 1 to 200. */
export function normalizeCapacity(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const n = Math.floor(value);
  return n >= 1 && n <= 200 ? n : null;
}

/**
 * The video pipeline canary's fixture offering (docs/video-robustness-plan-
 * 2026-08-18.md section 3) carries `canary: true` and must never render on
 * any family, dancer, or staff surface - no member, no roster, no guardian
 * would ever legitimately see it, but every offering-listing surface in the
 * Hub ultimately flows through useOfferings() below, so this is the one
 * choke point that keeps a synthetic nightly test class off every class
 * list, belt and braces on top of the fixture simply having no real roster.
 * Mirror of isPublicEligible's own canary check in
 * netlify/functions/publicClass.mjs (a .mjs function cannot import this .ts
 * module) - change one, change both; both are pinned by their own tests.
 */
export function isCanaryOfferingData(value: unknown): boolean {
  return Boolean(value && typeof value === 'object' && (value as Record<string, unknown>).canary === true);
}

export function normalizeOfferingDoc(id: string, value: unknown): OfferingDoc {
  const raw = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const kind = explicitOfferingKind({ kind: raw.kind as OfferingKind | null });
  // Computed once: this normalizer runs for every class on every snapshot, and
  // the alternate list is filtered against it below.
  const studentIds = normalizeStringArray(raw.studentIds);
  const rosterIds = new Set(studentIds);
  const teacherUid = cleanString(raw.teacherUid);
  const teacherNames: Record<string, string> = {};
  if (raw.teacherNames && typeof raw.teacherNames === 'object' && !Array.isArray(raw.teacherNames)) {
    for (const [uid, name] of Object.entries(raw.teacherNames as Record<string, unknown>)) {
      const cleanName = cleanString(name);
      if (uid.trim() && cleanName) teacherNames[uid] = cleanName;
    }
  }
  // The sub claim lane's temporary-seat ledger: see the return below.
  const subSeats = normalizeSubSeats(raw.subSeats);
  // Same string-only cleaning as teacherNames above, and just as
  // load-bearing: the rules only bound this map's SIZE (values are
  // uncheckable there), so this is where a non-string value is stopped
  // before some read site calls .trim() on it. Review 2026-08-19 caught the
  // first cut of the assistantNames build omitting the field here entirely,
  // which silently disconnected every read site while the writes went
  // through - the doc carried data no screen could ever show.
  const assistantNames: Record<string, string> = {};
  if (raw.assistantNames && typeof raw.assistantNames === 'object' && !Array.isArray(raw.assistantNames)) {
    for (const [uid, name] of Object.entries(raw.assistantNames as Record<string, unknown>)) {
      const cleanName = cleanString(name);
      if (uid.trim() && cleanName) assistantNames[uid] = cleanName;
    }
  }
  return {
    id,
    signupEventId: cleanString(raw.signupEventId) ?? undefined,
    name: cleanString(raw.name) ?? 'Untitled class',
    kind,
    style: normalizeStyle(raw.style),
    level: normalizeLevel(raw.level),
    teacherUid,
    coTeacherUids: normalizeCoTeacherUids(raw.coTeacherUids, teacherUid),
    assistantUids: Array.isArray(raw.assistantUids)
      ? raw.assistantUids.filter((v: unknown): v is string => typeof v === 'string')
      : [],
    teacherName: cleanString(raw.teacherName),
    teacherNames,
    // THE TEMPORARY-SEAT LEDGER, which this normalizer used to drop. Without
    // it no screen could tell a sub seat from a real co-teacher: the class a
    // sub covered once sat on her calendar every week of the window
    // (feedback gxpVbdu6), and the Director's "Keep permanently" panel on
    // /offerings, which reads o.subSeats, could never appear. Undefined when
    // the class has no seat, and then the key is left off entirely, so a
    // class nobody is covering reads exactly as it did before.
    ...(subSeats ? { subSeats } : {}),
    assistantNames,
    studentIds,
    // Kept honest against the roster: an alternate who has been unenrolled is
    // not standing by for anything, and leaving a stale id here would inflate
    // every alternate count on the page.
    alternateStudentIds: normalizeStringArray(raw.alternateStudentIds).filter((sid) =>
      rosterIds.has(sid)
    ),
    alternateInfo: normalizeAlternateInfo(raw.alternateInfo),
    recitalNumber: cleanString(raw.recitalNumber) ?? undefined,
    recitalTime: cleanString(raw.recitalTime) ?? undefined,
    musicLinks: sortMediaNewestFirst(Array.isArray(raw.musicLinks) ? raw.musicLinks : []),
    musicReviewStatus: normalizeMusicReviewStatus(raw.musicReviewStatus),
    videoLinks: sortMediaNewestFirst(Array.isArray(raw.videoLinks) ? raw.videoLinks : []),
    danceTracks: normalizeDanceTracks(Array.isArray(raw.danceTracks) ? raw.danceTracks : []),
    capacity: normalizeCapacity(raw.capacity),
    ageGroup: cleanString(raw.ageGroup),
    ageBands: normalizeAgeBands(raw.ageBands),
    audienceLabel: cleanString(raw.audienceLabel),
    familyNote: cleanString(raw.familyNote),
    // Passed through as-is and NOT defaulted: a missing stamp has to stay
    // missing so every reader falls back to updatedAt rather than sorting a
    // pre-2026-08-18 note to the beginning of time.
    familyNoteUpdatedAt: raw.familyNoteUpdatedAt as Timestamp | FieldValue | undefined,
    familyNoteAttachments: Array.isArray(raw.familyNoteAttachments)
      ? raw.familyNoteAttachments as MessageAttachment[]
      : undefined,
    themeColor: cleanString(raw.themeColor),
    photoUrl: cleanString(raw.photoUrl),
    avatarPhotoUrl: cleanString(raw.avatarPhotoUrl),
    coverFocusY:
      typeof raw.coverFocusY === 'number' && Number.isFinite(raw.coverFocusY)
        ? Math.min(100, Math.max(0, raw.coverFocusY))
        : null,
    compLook: normalizeCompLook(raw.compLook),
    songName: cleanString(raw.songName),
    nameSetByHuman: raw.nameSetByHuman === true,
    studioProName: cleanString(raw.studioProName),
    songNameSetByHuman: raw.songNameSetByHuman === true,
    helperDanceId: cleanString(raw.helperDanceId),
    createdAt: raw.createdAt as Timestamp | FieldValue,
    updatedAt: raw.updatedAt as Timestamp | FieldValue,
    createdBy: cleanString(raw.createdBy) ?? '',
  };
}
export interface UseOfferingsResult {
  loading: boolean;
  offerings: OfferingDoc[];
  error: string | null;
  /**
   * True while this list came from the local cache and the server has not
   * confirmed it yet (cold cache, weak signal, offline, or a write still in
   * flight). A required field, not optional, so every caller has to decide
   * what to do with it rather than silently assuming fresh - see
   * useOpenInjuryNotes for the same shape. Only accurate (able to resolve
   * from a stale true back to false on a content-identical server confirm)
   * when trackFromCache is passed true; existing callers that do not ask
   * for it get the exact same subscription as before this field existed.
   */
  fromCache: boolean;
}

/** The state one shared offerings subscription hands every caller attached
 *  to it. Replaced whole on each snapshot, never mutated, so React sees a
 *  new identity and re-renders. */
type OfferingsState = UseOfferingsResult;

const OFFERINGS_INITIAL: OfferingsState = {
  loading: true,
  offerings: [],
  error: null,
  // Starts true: nothing is server-confirmed until a snapshot says so.
  fromCache: true,
};
const OFFERINGS_DISABLED: OfferingsState = {
  loading: false,
  offerings: [],
  error: null,
  fromCache: true,
};
const OFFERINGS_NO_DB: OfferingsState = {
  loading: false,
  offerings: [],
  error: 'Firestore not initialized.',
  fromCache: true,
};

interface SharedOfferingsSubscription {
  key: string;
  state: OfferingsState;
  /**
   * Every attached caller, mapped to whether it asked for metadata-only
   * events. A caller that did not ask is never notified of one; see
   * publishOfferingsState.
   */
  subscribers: Map<(state: OfferingsState) => void, boolean>;
  unsubscribe: () => void;
}

/**
 * ONE LIVE OFFERINGS QUERY PER SCREEN, NOT ONE PER COMPONENT.
 *
 * useOfferings is called all over the app - FamilyCommandCenter alone calls
 * it on the parent home, and several panels within a staff screen call it
 * independently. Each call used to open its OWN studio-wide listener on
 * `offerings`, so a screen with four callers paid four full reads of roughly
 * a hundred class documents and held four listeners open. Firestore bills the
 * initial read of every listener; identical queries are not deduplicated for
 * you.
 *
 * That is a straight multiplier on the reads behind the daily quota the Hub
 * hit on the class night of 2026-09-08, and it buys nothing: every caller
 * wants the same list.
 *
 * Callers on the same readEpoch now share one subscription and one cache. The
 * count is what releases it: the listener is torn down when the LAST caller
 * unmounts, so a screen nobody is looking at holds nothing open.
 *
 * trackFromCache USED TO BE PART OF THE KEY, and that was itself a
 * multiplier. One caller wanting `fromCache` to be trustworthy opened a
 * SECOND full listener over the same hundred documents, so a page with a
 * tracking card and a plain one paid twice, and merely opening the countdown
 * editor on Studio settings (its class picker is a plain caller) added
 * another. The flag now changes only who gets NOTIFIED: the listener always
 * runs with includeMetadataChanges, which costs no extra reads, and
 * publishOfferingsState below drops a metadata-only event for every caller
 * that did not ask for it. So the guarantee the split existed to give - a
 * caller that did not ask for the extra events does not receive them, and
 * nothing downstream of it recomputes - is kept, without the second read.
 */
const sharedOfferingsSubscriptions = new Map<string, SharedOfferingsSubscription>();

/**
 * Hand a new state to the callers entitled to it.
 *
 * `metadataOnly` means the class list itself did not change: the snapshot
 * carried no document changes and only told us whether the studio has
 * confirmed what we already had. The state IS updated for everyone (a caller
 * mounting later reads the accurate one), but only callers that opted in are
 * re-rendered, and the offerings array keeps its identity, so a memo or an
 * effect one hop downstream of it does not re-run.
 */
function publishOfferingsState(
  sub: SharedOfferingsSubscription,
  state: OfferingsState,
  metadataOnly = false
) {
  sub.state = state;
  for (const [subscriber, wantsMetadata] of sub.subscribers) {
    if (metadataOnly && !wantsMetadata) continue;
    subscriber(state);
  }
}

function acquireOfferingsSubscription(
  firestore: NonNullable<typeof db>,
  readEpoch: number
): SharedOfferingsSubscription {
  const key = `epoch:${readEpoch}`;
  const existing = sharedOfferingsSubscriptions.get(key);
  if (existing) return existing;

  const sub: SharedOfferingsSubscription = {
    key,
    state: OFFERINGS_INITIAL,
    subscribers: new Map(),
    unsubscribe: () => {},
  };
  sharedOfferingsSubscriptions.set(key, sub);

  const q = query(collection(firestore, 'offerings'), orderBy('name'));
  // Cache normalized docs by id across snapshots (perf round, audit #5 -
  // same docChanges()-keyed identity pattern as useAllTasks): a write to
  // ONE offering must not hand every OTHER unchanged offering a brand-new
  // object identity, which forced downstream memos and rows to recompute
  // across all ~100 classes on every snapshot.
  const cache = new Map<string, OfferingDoc>();
  sub.unsubscribe = onSnapshot(
    q,
    // ALWAYS ON, and it costs no reads. includeMetadataChanges changes only
    // how many times the local SDK calls back, never how many documents are
    // billed, and it is the only way `fromCache` reliably flips to false when
    // the server confirms a cached list whose content did not change. Who
    // HEARS about those extra callbacks is decided in publishOfferingsState,
    // so a caller that did not ask still gets the exact event volume it had
    // before this option existed.
    { includeMetadataChanges: true },
    (snap) => {
      const changes = snap.docChanges();
      // METADATA ONLY: no document changed, the studio just confirmed (or
      // stopped confirming) the list we already have. Same array, same error,
      // same loading, new fromCache. Rebuilding the array here would hand
      // every downstream memo a fresh identity for nothing, which is the
      // identity-churn class this file was bitten by before.
      if (changes.length === 0 && !sub.state.loading && sub.state.error === null) {
        publishOfferingsState(
          sub,
          { ...sub.state, fromCache: snap.metadata.fromCache },
          true
        );
        return;
      }
      // Read per snapshot, not once per subscription: the list is shared
      // across a tab's whole life, and who is signed in decides whether the
      // live smoke test class is visible (lib/smokeFixture.ts).
      const viewerUid = getAuth().currentUser?.uid;
      for (const change of changes) {
        // The video pipeline canary's fixture offering never joins this
        // cache - see isCanaryOfferingData's own comment. Treating it the
        // same as a removal (rather than simply skipping the set below)
        // also clears it out if a doc were ever flipped canary:true after
        // already being cached, which the create-if-absent seed never
        // does but this guard does not need to assume.
        if (change.type === 'removed' || isHiddenFixtureData(change.doc.data(), viewerUid)) {
          cache.delete(change.doc.id);
        } else {
          cache.set(change.doc.id, normalizeOfferingDoc(change.doc.id, change.doc.data()));
        }
      }
      const liveDocs = snap.docs.filter((d) => !isHiddenFixtureData(d.data(), viewerUid));
      const liveIds = new Set(liveDocs.map((d) => d.id));
      for (const id of cache.keys()) {
        if (!liveIds.has(id)) cache.delete(id);
      }
      publishOfferingsState(sub, {
        offerings: liveDocs.map((d) => cache.get(d.id) ?? normalizeOfferingDoc(d.id, d.data())),
        // A retry that leaves the error set is not a retry. See readEpoch.ts.
        error: null,
        fromCache: snap.metadata.fromCache,
        loading: false,
      });
    },
    (err) => {
      publishOfferingsState(sub, {
        offerings: sub.state.offerings,
        error: err.message,
        fromCache: true,
        loading: false,
      });
    }
  );
  return sub;
}

function releaseOfferingsSubscription(sub: SharedOfferingsSubscription) {
  if (sub.subscribers.size > 0) return;
  // Only drop the map entry if it is still THIS subscription. A readEpoch
  // bump replaces the entry under the same key shape, and tearing down a
  // successor here would leave its callers listening to nothing.
  if (sharedOfferingsSubscriptions.get(sub.key) === sub) {
    sharedOfferingsSubscriptions.delete(sub.key);
  }
  sub.unsubscribe();
}

/** Test seam: the number of live shared subscriptions. Nothing in the app
 *  reads this; it exists so a test can assert the count rather than infer it. */
export function activeOfferingsSubscriptionCount(): number {
  return sharedOfferingsSubscriptions.size;
}

/**
 * Subscribe to every class the studio offers. Director-only by rule -
 * the listener fires permission-denied for anyone else.
 *
 * Every caller with the same options shares ONE live query; see
 * sharedOfferingsSubscriptions above for why.
 *
 * `trackFromCache` opts a caller into the metadata-only events, so
 * `fromCache` reliably flips back to false even when the server confirms a
 * cache snapshot whose content did not change (see useDayReservations'
 * doc comment - without this a cache-empty-then-server-empty list would
 * never fire a second event and `fromCache` would stay stuck true).
 * Defaults to false so the many existing callers of useOfferings() keep
 * the identical event volume they always had. It no longer splits the
 * subscription: opting in costs nothing and every caller shares one read.
 */
export function useOfferings(enabled = true, trackFromCache = false): UseOfferingsResult {
  const [state, setState] = useState<OfferingsState>(OFFERINGS_INITIAL);
  const readEpoch = useReadEpoch();

  useEffect(() => {
    if (!enabled) {
      setState(OFFERINGS_DISABLED);
      return;
    }
    if (!db) {
      setState(OFFERINGS_NO_DB);
      return;
    }
    const sub = acquireOfferingsSubscription(db, readEpoch);
    // Whatever the shared subscription already knows, straight away: a second
    // caller mounting onto a settled list must not draw a spinner over data
    // that is already here.
    setState(sub.state);
    const receive = (next: OfferingsState) => setState(next);
    sub.subscribers.set(receive, trackFromCache);
    return () => {
      sub.subscribers.delete(receive);
      releaseOfferingsSubscription(sub);
    };
    // readEpoch: a failed onSnapshot never retries itself. Only moves when
    // someone presses Try again under a visible failure, so a healthy session
    // pays nothing. See lib/readEpoch.ts.
  }, [enabled, trackFromCache, readEpoch]);

  return state;
}

export interface UseResolvedOfferingsByIdResult {
  /** `liveOfferingsById`, widened with any individually resolved doc that
   * came back found. See lib/offeringResolution.ts' doc comment for why a
   * doc can be readable-and-real yet absent from useOfferings()' list. */
  offeringsById: Map<string, OfferingDoc>;
  /** True while `id` has neither been found (live or resolved) nor
   * confirmed missing yet - the "still checking" state a caller needs to
   * tell apart from "confirmed gone" (see honestLabel.ts's resolveLinkedLabel,
   * which takes exactly this boolean). */
  isResolving: (id: string) => boolean;
}

/**
 * Attendance report, and Attendance report only, cares whether a class name
 * for an OLD mark is missing versus just not-yet-loaded. This hook does the
 * looking: for any id in `neededIds` that useOfferings' live list does not
 * cover, it fetches that one doc directly (no orderBy, no field
 * requirement) and folds a FOUND result back in - a genuinely deleted
 * offering (getDoc's snap.exists() === false) is remembered as confirmed
 * missing so it is never re-fetched, and the caller's normal "no offering
 * for this id" honest-label path applies unchanged.
 *
 * `neededIds` is read through a stable joined key so passing a fresh array
 * literal every render (the normal shape for "ids referenced by these
 * rows") does not restart the effect - same pattern as
 * useTonightAttendanceMarks' idsKey in lib/attendance.ts.
 */
export function useResolvedOfferingsById(
  liveOfferingsById: Map<string, OfferingDoc>,
  neededIds: readonly string[]
): UseResolvedOfferingsByIdResult {
  const [resolvedExtra, setResolvedExtra] = useState<Map<string, OfferingDoc | null>>(
    () => new Map()
  );
  // Ids whose individual getDoc came back with an ERROR. Kept apart from
  // resolvedExtra on purpose - "confirmed gone" must only ever mean the read
  // actually succeeded and came back empty - but tracked all the same, so a
  // failed read is not retried on its own.
  //
  // TASK-QUEUE.md F6, 2026-09-09: resolvedExtra IS an effect dependency, so
  // before this every id that succeeded re-fired every id that had already
  // failed - bounded at failures x successes rather than infinite, but still
  // an uncapped retry of a read the server just refused, on a page (/students)
  // that now runs this over every membership id on it. Same shape as the
  // useOfferingBookingDates bug fixed in #956 (lib/reservations.ts), which is
  // the same shape as the useMyThreads amplifier behind the 2026-09-08 quota
  // incident that cost a family a class night (docs/lessons.md). Copying that
  // fix exactly: a recorded failure is skipped for the life of the mount, and
  // the ONE thing that clears it is a read-epoch bump, which is the studio's
  // own deliberate Try again (lib/readEpoch.ts). No timer, no backoff ladder,
  // nothing automatic.
  const [failedIds, setFailedIds] = useState<Set<string>>(() => new Set());
  // Tracks lookups already in flight so two renders asking for the same
  // missing id inside one round trip fire one getDoc, not two.
  const inFlightRef = useRef<Set<string>>(new Set());
  const neededKey = neededIds.join(',');
  const readEpoch = useReadEpoch();
  const lastEpochRef = useRef(readEpoch);
  if (lastEpochRef.current !== readEpoch) {
    lastEpochRef.current = readEpoch;
    if (failedIds.size > 0) setFailedIds(new Set());
  }

  useEffect(() => {
    if (!db) return;
    const database = db;
    const toFetch = idsStillNeedingResolution(
      neededIds,
      liveOfferingsById,
      resolvedExtra
    ).filter((id) => !inFlightRef.current.has(id) && !failedIds.has(id));
    for (const id of toFetch) {
      inFlightRef.current.add(id);
      getDoc(doc(database, 'offerings', id))
        .then((snap) => {
          setResolvedExtra((prev) => {
            const next = new Map(prev);
            next.set(id, snap.exists() ? normalizeOfferingDoc(id, snap.data()) : null);
            return next;
          });
        })
        .catch(() => {
          // Still never recorded as an answer - see the doc comment on
          // failedIds above - but IS recorded as a failure now, so it is
          // skipped rather than re-fetched every time a sibling id resolves.
          setFailedIds((prev) => new Set(prev).add(id));
        })
        .finally(() => {
          inFlightRef.current.delete(id);
        });
    }
    // neededIds itself is intentionally not a dependency - neededKey is its
    // stable proxy, matching useTonightAttendanceMarks' idsKey pattern.
    // failedIds is deliberately not a dependency either: it only ever grows
    // within a mount (skipped ids need no re-run of their own), and the epoch
    // bump above clears it and re-runs through readEpoch instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neededKey, liveOfferingsById, resolvedExtra, readEpoch]);

  const offeringsById = useMemo(
    () => widenOfferingsById(liveOfferingsById, resolvedExtra),
    [liveOfferingsById, resolvedExtra]
  );

  const isResolving = (id: string) =>
    !liveOfferingsById.has(id) && !resolvedExtra.has(id);

  return { offeringsById, isResolving };
}

/**
 * Dancer-login class list. The matching Firestore rule requires this exact
 * array-contains scope, so a child-only login never subscribes to the full
 * studio catalog.
 */
export function useDancerLoginOfferings(studentId: string | null | undefined) {
  const [offerings, setOfferings] = useState<OfferingDoc[]>([]);
  const [loading, setLoading] = useState(Boolean(studentId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!db || !studentId) {
      setOfferings([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const scoped = query(
      collection(db, 'offerings'),
      where('studentIds', 'array-contains', studentId),
    );
    return onSnapshot(
      scoped,
      (snap) => {
        setOfferings(
          snap.docs
            // The canary fixture never carries a real studentId in
            // studentIds, so this array-contains scope already excludes it
            // in practice; filtered anyway for the same belt-and-braces
            // reason useOfferings() above does.
            .filter((entry) => !isHiddenFixtureData(entry.data(), getAuth().currentUser?.uid))
            .map((entry) => normalizeOfferingDoc(entry.id, entry.data()))
            .sort((a, b) => a.name.localeCompare(b.name)),
        );
        setError(null);
        setLoading(false);
      },
      (err) => {
        setOfferings([]);
        setError(err.message);
        setLoading(false);
      },
    );
  }, [studentId]);

  return { offerings, loading, error };
}

export interface NewOfferingInput {
  name: string;
  kind?: OfferingKind | null;
  style: DanceStyle;
  level: Level;
  teacherUid: string | null;
  coTeacherUids?: string[];
  teacherName?: string | null;
  assistantUids?: string[];
  /** See OfferingDoc.assistantNames. Resolved by the caller's own directory. */
  assistantNames?: Record<string, string>;
  themeColor?: string | null;
  photoUrl?: string | null;
  danceTracks?: DanceTrack[];
  capacity?: number | null;
  ageGroup?: string | null;
  ageBands?: AgeBand[];
  /** Comp-only: the song a comp dance/comp class is set to. */
  songName?: string | null;
}

/**
 * Hook returning a stable create function bound to the current Director.
 * Returns null if the user isn't signed in (the page guards on this so
 * this should not happen in practice).
 */
export function useCreateOffering() {
  const { user } = useAuth();
  return async (input: NewOfferingInput): Promise<string> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    const assistantLimitError = assistantAccountLimitError(input.assistantUids ?? []);
    if (assistantLimitError) throw new Error(assistantLimitError);
    const firestore = db;
    const ref = doc(collection(firestore, 'offerings'));
    await createOfferingViaServer(user, ref.id, {
      name: input.name.trim(),
      kind: input.kind ?? 'class',
      style: input.style,
      level: input.level,
      teacherUid: input.teacherUid,
      teacherName: input.teacherName?.trim() || null,
      coTeacherUids: normalizeCoTeacherUids(input.coTeacherUids, input.teacherUid),
      // Refused above when oversized. Never silently slice a linked account
      // pair or a stale assignment off an otherwise unrelated class save.
      assistantUids: input.assistantUids ?? [],
      // Caller-resolved, unlike the server-resolved teaching team: a dancer-login
      // assistant's users doc is refused to the transaction's tx.get
      // pattern for most viewers, and only a Director creates classes, so
      // the Director's own directory (which holds every account) is the
      // honest source. Only names for uids actually assigned survive.
      assistantNames: Object.fromEntries(
        Object.entries(input.assistantNames ?? {}).filter(
          ([uid, name]) =>
            (input.assistantUids ?? []).includes(uid) && !!name?.trim()
        )
      ),
      themeColor: input.themeColor?.trim() || null,
      photoUrl: input.photoUrl?.trim() || null,
      danceTracks: normalizeDanceTracks(input.danceTracks),
      capacity: normalizeCapacity(input.capacity),
      ageGroup: input.ageGroup?.trim() || null,
      ageBands: normalizeAgeBands(input.ageBands),
      songName: input.songName?.trim() || null,
    });
    return ref.id;
  };
}

/**
 * Every song-name save in the Hub goes through here: the teacher's lane on
 * ProductionNeedsCard, the class page, the class catalog, and the production
 * grid all patch the offering. So the Directors' alert hangs off THIS function
 * rather than off four screens, and a fifth screen added later inherits it
 * instead of quietly not sending.
 *
 * A save that repeats the song name already stored still calls out, and that
 * is fine: the server gives one card per class per distinct song name per
 * Director, so a re-save of an unchanged name resolves to a card that already
 * exists and nobody is told twice. Rec classes carry a null song name and
 * never reach this branch at all.
 */
function carriesEnteredSongName(
  fields: Partial<Omit<OfferingDoc, 'id' | 'createdAt' | 'createdBy'>>
): boolean {
  return typeof fields.songName === 'string' && fields.songName.trim().length > 0;
}

export async function updateOffering(
  id: string,
  fields: Partial<Omit<OfferingDoc, 'id' | 'createdAt' | 'createdBy'>>,
  /** assistantBase: the Jr assistants an editor opened on, sent with a change to them (bug B10). */
  options: { assistantBase?: string[] } = {}
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  const user = getAuth().currentUser;
  if (!user) throw new Error('Sign in required.');
  if (fields.assistantUids) {
    const assistantLimitError = assistantAccountLimitError(fields.assistantUids);
    if (assistantLimitError) throw new Error(assistantLimitError);
  }
  await updateOfferingViaServer(user, id, fields, options);
  if (carriesEnteredSongName(fields)) await notifyProductionUpdate(id, 'song-name');
}

export async function promoteOfferingSubSeat(id: string, seatUid: string): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  const user = getAuth().currentUser;
  if (!user) throw new Error('Sign in required.');
  await promoteOfferingSubSeatViaServer(user, id, seatUid);
}

/**
 * Add or remove one Jr assistant without rewriting the whole assistant list.
 *
 * THROUGH THE SERVER LANE (bug B10). Bobbie, 2026-08-28: "Admin managers and
 * directors." attach a Jr Staff; the rules only ever let the Director write
 * this list, so the Director, a Manager and front desk all go through
 * offeringWrite's setAssistant action (lib/offeringWrite.mjs,
 * setOfferingAssistant). It runs in one transaction, so a simultaneous
 * assignment from another screen is never lost, and it:
 *   - refuses anyone front desk or a Manager adds who is not an active Jr
 *     Staff;
 *   - treats her dancer sign-in and her email sign-in as ONE person: attaching
 *     her when either is already on the class changes nothing, and taking her
 *     off takes both off;
 *   - refreshes who can see the class's bookings after a change (her family
 *     belongs on them now), which the client used to ask the Director-only
 *     refreshClassVisibility endpoint for.
 *
 * Assigning WITHOUT a name leaves any stored name alone (review finding F6):
 * the caller not knowing a name is not evidence the stored one is wrong. The
 * caller passes a family-safe name only; see OfferingDoc.assistantNames.
 */
export async function setOfferingAssistant(
  offeringId: string,
  assistantUid: string,
  assigned: boolean,
  assistantName?: string
): Promise<void> {
  if (!offeringId || !assistantUid) throw new Error('Choose a class and a Jr Staff member.');
  const user = getAuth().currentUser;
  if (!user) throw new Error('Sign in required.');
  const cleanName = assistantName?.trim() || null;
  await setOfferingAssistantViaServer(user, offeringId, assistantUid, assigned, assigned ? cleanName : null);
}

/**
 * Ceiling the offerings rule enforces on musicLinks and videoLinks. Raised
 * 50 -> 200 (Bobbie, 2026-08-07, "unlimited-feel uploads" - a class holds a
 * season's worth of drafts, not just one final cut per dance) alongside the
 * matching `.size() <= 200` checks in shared/firestore.rules - the two MUST
 * move together, since the client constant is only a friendly early message
 * and the rules are what actually enforce it.
 */
export const MEDIA_LINKS_MAX = 200;

/**
 * Append ONE media link, server-side, without resending the list.
 *
 * Every add path used to write `[...offering.videoLinks, newLink]`, and that
 * array is not what is stored: normalizeOffering runs sortMediaNewestFirst on
 * read, which reorders, trims strings, mints a synthetic id for a link that
 * has none, defaults a missing addedAt to 0, drops optional keys it finds
 * empty, and drops any link whose url is not http(s) at all. So the "unchanged"
 * entries in that write are frequently NOT byte-identical to the stored ones,
 * and on a class with one legacy link the array quietly came back different.
 *
 * That was survivable while every writer could rewrite the whole list. It is
 * not survivable now: the Jr assistant lane in shared/firestore.rules is
 * ADD-ONLY, enforced as `hasAll` against the stored list, so a normalized
 * rewrite reads as a removal and the assistant's perfectly ordinary upload is
 * denied. arrayUnion appends to the array Firestore actually holds, so
 * preservation is exact by construction and no client-side canonicalization is
 * needed. PROVEN in the emulator rather than assumed - hub/tests/
 * firestore-rules.test.mjs asserts arrayUnion is allowed on this lane AND that
 * arrayRemove is refused, which together show rules do see the transformed
 * value rather than skipping the field.
 *
 * It also removes a lost update: two people adding a clip at the same time
 * used to have the slower write silently overwrite the faster one's.
 */
export async function appendOfferingMediaLink(
  id: string,
  kind: 'music' | 'video',
  // attachmentReservationId is transient wire-only (H-3): present only on a
  // freshly gateway-uploaded video, read by writeStaffVideoLink to verify
  // and consume the reservation, never itself stored on videoLinks.
  link: MediaLink & { attachmentReservationId?: string }
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  const user = getAuth().currentUser;
  if (!user) throw new Error('Sign in required.');
  if (kind === 'music') {
    await writeOfferingMusicViaServer(user, { action: 'appendMediaLink', offeringId: id, kind, link });
    // Packet F2: a Director can now save a NEW music track already marked
    // final, the same one-tap add video always allowed. That still has to
    // tell the Directors, exactly once, and as the RIGHT card - "final cut"
    // (it is done), never "music-upload" (it still needs a second person) -
    // never both. Before this packet finalCut could never be true here, so
    // this branch never existed and only the "needs a final cut" alert did.
    if (!isPendingMusicLink(link)) {
      await notifyProductionUpdate(id, link.finalCut === true ? 'final-cut' : 'music-upload', link.id);
    }
  } else {
    await appendOfferingMediaLinkViaServer(user, id, kind, link);
  }
}

/**
 * Replace exactly one uploaded-video reservation through the server write
 * lane. Unlike generic transforms this commits the finished public media row,
 * canonical class post, and durable notification operation together.
 */
export async function finalizeOfferingVideoUpload(
  id: string,
  reservationId: string,
  // attachmentReservationId is transient wire-only (H-3) - see
  // appendOfferingMediaLink's own comment.
  link: MediaLink & { attachmentReservationId?: string },
  expectedUploaderUid?: string,
): Promise<{ notificationPostId?: string | null; notificationOperationId?: string | null; replayed?: boolean }> {
  if (!db) throw new Error('Firestore not initialized.');
  const user = getAuth().currentUser;
  if (!user) throw new Error('Sign in required.');
  if (expectedUploaderUid && user.uid !== expectedUploaderUid) {
    throw new Error('Sign in with the account that started this upload to finish linking it.');
  }
  return finalizeOfferingVideoUploadViaServer(user, id, reservationId, link);
}

/**
 * DELETE A CLASS, AND TAKE ITS CALENDAR WITH IT.
 *
 * Bobbie, 2026-09-15 6:53am Central, verbatim: "When I click on this class
 * that has been deleted but it is still showing up in the calendar". This
 * function was a bare deleteDoc. The comment that used to sit here said the
 * missing cascade was deliberate and belonged in a Netlify Function; what it
 * actually bought was a class that disappeared from the catalog and went on
 * teaching itself on the schedule forever, with its rooms still locked, its
 * dancers still linked to it, and every tap on it landing on "Class not
 * found".
 *
 * IT IS A CLIENT CASCADE AFTER ALL, and the reason is that deleteStudent
 * (lib/students.ts) already proved the shape works: every dependent is ended
 * through the lane that owns it, the irreversible removal goes LAST, and a
 * refusal anywhere leaves the record exactly as it was rather than half
 * deleted. Nothing new was asked of the rules to do it - a Director may
 * already delete a reservation, its slot documents, a classSeries and an
 * offering from the client (shared/firestore.rules), which is the whole
 * reason this did not have to become a server function.
 *
 * THE ORDER, and why each step is where it is:
 *
 *   1. MEMBERSHIPS, through the setEnrollment server lane, exactly as
 *      deleteStudent does - the one place that writes both sides of the link
 *      atomically. Both sides are ASKED, too: the class's own studentIds AND
 *      every dancer whose memberships array names this class. The two can
 *      disagree, and a dancer holding a membership the roster never knew
 *      about is precisely the half-link that survives a delete and becomes
 *      permanent (setEnrollment.mjs's own comments record both directions of
 *      that bug).
 *   2. WEEKLY TIMES, through deleteClassSeries, which already cancels each
 *      series' future bookings and frees their room locks before removing the
 *      series document. Not reimplemented here; called.
 *   3. LOOSE BOOKINGS. Step 2 only reaches what a series generated. A
 *      one-off session somebody booked by hand for this class carries no
 *      seriesId and would otherwise be left standing - the exact leftover
 *      Bobbie was looking at.
 *   4. THE CLASS ITSELF, last, and only if every step above came back clean.
 *
 * PAST SESSIONS STAY AS HISTORY, the same boundary deleteClassSeries keeps
 * and the same one the Remove confirm on a weekly time already promises her.
 *
 * NOBODY IS TOLD. setEnrollment sends no notification of any kind on unenroll
 * (checked in netlify/functions/setEnrollment.mjs: it writes the two sides,
 * an audit entry, and the family visibility snapshots, and there is no notify
 * call in the file), and cancelReservation posts no notice either. That
 * matches deleteClassSeries and the Studio Settings clean-up card, which are
 * both silent today.
 *
 * A REFUSED WRITE NEVER ABANDONS THE REST OF THE BATCH, and never lets the
 * class document go. Everything that can be cleared is cleared, what could
 * not be is collected, and then this throws naming it - so a retry picks up
 * where it stopped and a half-run leaves exactly the shape the Studio
 * Settings clean-up card repairs (lib/staleClassBookings.ts' 'classRemoved').
 *
 * WHAT THIS CASCADE STILL DOES NOT REACH, kept from the comment that stood
 * here before it, because the risk it named has NOT gone away for these:
 * `subRequests`, `attendance`, `costumes` and `tasks` can all still hold a
 * now-orphaned offeringId after this call. subRequests alone has no
 * offeringId + status entry in shared/firestore.indexes.json, so there is no
 * safe way to query them by class from here at all. THE STANDING RULE FOR
 * EVERY READER OF A POSSIBLY-DELETED offeringId THEREFORE STILL HOLDS
 * (SubRequests.tsx's classLabelForRequest / resolveLinkedCount, for one):
 * treat a missing offering as "removed", never assume its studentIds or its
 * name are real, and never print the offeringId itself as if it were the
 * class name. lib/deletedClass.ts is where that decision now lives for any
 * screen that wants it, and its three-way answer (present / checking /
 * removed) is the reason "not in the offerings map" is never on its own
 * proof that a class is gone.
 */
export interface DeleteOfferingResult extends DeleteOfferingCounts {
  /** When the one-hour Undo ends, in ms (bug B22); null when the server did not say. */
  undoUntil: number | null;
  /** Dancers whose link to this class was ended, by student id. */
  releasedStudentIds: string[];
  /** Weekly times removed, by classSeries id. */
  removedSeriesIds: string[];
  /** Every future session taken off the calendar, from steps 2 and 3 alike. */
  cancelledReservationIds: string[];
}

/** A dancer `deleteOffering`'s final safety check found still linked. */
interface StrayMembershipEntry {
  id: string;
  /** Never invented: 'A dancer' when a name field is missing, same fallback deleteStudent uses. */
  name: string;
}

/**
 * Re-reads BOTH sides of the membership link for one offering, right before
 * a write that assumes there is nothing left: the offering's own roster and
 * every student whose `memberships` array still names it. Only ever called
 * from `deleteOffering`'s final safety check, immediately before the
 * `deleteDoc` - see that call site for why.
 *
 * The client-SDK twin of `offeringHasStrayMembership` in
 * netlify/functions/assistant.mjs, which is server-only (Admin SDK) and so
 * cannot be imported here directly. Same query shape, same reasoning
 * (splitByStrayMembership's comment there): the roster side alone is not
 * enough, because Visibility is membership-driven from the student's own
 * record, never the offering's cached list.
 */
async function offeringStrayMembershipEntries(
  firestore: NonNullable<typeof db>,
  offeringId: string
): Promise<StrayMembershipEntry[]> {
  const [offeringSnap, membershipSnap] = await Promise.all([
    getDoc(doc(firestore, 'offerings', offeringId)),
    getDocs(query(collection(firestore, 'students'), where('memberships', 'array-contains', offeringId))),
  ]);
  // A CACHE-ONLY ANSWER IS AN UNKNOWN, NOT A CLEAN ONE - the same rule every
  // read earlier in deleteOffering already keeps. This is the LAST check
  // before the write that assumes nothing is left; answering it from a
  // saved copy could say "nothing stray" while the server would not, which
  // is exactly the silent ghost this whole guard exists to close.
  if (offeringSnap.metadata.fromCache || membershipSnap.metadata.fromCache) {
    throw new Error('this device\'s saved copy, not the server, so the last check before deleting could not run');
  }
  const rosterIds = Array.isArray(offeringSnap.data()?.studentIds)
    ? (offeringSnap.data()!.studentIds as unknown[]).filter((v): v is string => typeof v === 'string' && v.length > 0)
    : [];
  const nameById = new Map<string, string>();
  const nameFrom = (data: { firstName?: unknown; lastName?: unknown } | undefined): string => {
    const full = `${cleanString(data?.firstName) ?? ''} ${cleanString(data?.lastName) ?? ''}`.trim();
    return full || 'A dancer';
  };
  for (const studentDoc of membershipSnap.docs) {
    nameById.set(studentDoc.id, nameFrom(studentDoc.data() as { firstName?: unknown; lastName?: unknown }));
  }
  const ids = Array.from(new Set([...rosterIds, ...membershipSnap.docs.map((d) => d.id)]));
  const missingIds = ids.filter((sid) => !nameById.has(sid));
  if (missingIds.length > 0) {
    const missingSnaps = await Promise.all(missingIds.map((sid) => getDoc(doc(firestore, 'students', sid))));
    missingSnaps.forEach((snap, index) => {
      const data = snap.exists() ? (snap.data() as { firstName?: unknown; lastName?: unknown }) : undefined;
      nameById.set(missingIds[index], nameFrom(data));
    });
  }
  return ids.map((sid) => ({ id: sid, name: nameById.get(sid) ?? 'A dancer' }));
}

export async function deleteOffering(id: string): Promise<DeleteOfferingResult> {
  if (!db) throw new Error('Firestore not initialized.');
  const firestore = db;
  const now = new Date();

  const offeringSnap = await getDoc(doc(firestore, 'offerings', id));
  const offeringName = (offeringSnap.exists() ? cleanString(offeringSnap.data()?.name) : null)
    || 'This class';
  // A CACHE-ONLY ANSWER IS AN UNKNOWN, NOT AN ANSWER - deleteStudent's rule,
  // and this function needs it for the same reason. Offline, getDoc and
  // getDocs both answer from the local cache WITHOUT throwing: an empty
  // cached roster, an empty cached series list and an empty cached booking
  // list would mean nothing is cancelled at all, while the deleteDoc is
  // queued locally and lands on reconnect - producing the exact ghost this
  // whole function exists to prevent, and producing it silently.
  if (offeringSnap.metadata.fromCache) {
    throw new Error(
      `${offeringName} could not be re-read from the server, so nothing was deleted. This device `
      + 'is working from a saved copy, which cannot say who is on this class or what it has '
      + 'booked. Nothing was changed. Check the connection and try again.'
    );
  }
  // A workshop's own class goes only with its workshop; see
  // workshopClassDeleteCopy. Refused here, before a single write.
  if (cleanString(offeringSnap.data()?.signupEventId)) {
    throw new Error(workshopClassDeleteCopy(offeringName));
  }
  const rosterStudentIds = Array.isArray(offeringSnap.data()?.studentIds)
    ? (offeringSnap.data()!.studentIds as unknown[]).filter((v): v is string => typeof v === 'string' && v.length > 0)
    : [];

  // A COPY FIRST, BEFORE ANYTHING COMES OFF (bug B22). Bobbie, 2026-08-06:
  // "I want an undo button to be present for 1 hour.... and then a place to
  // completly undo maybe under studio settings.... but only up to a month".
  // The server keeps the class as it stands, every dancer on both sides of
  // the link, its weekly times and every upcoming booking with its room
  // holds (lib/deletedClasses.mjs), and the class itself is deleted by the
  // server at the end, which marks that copy deleted. No copy, no delete.
  const signedIn = getAuth().currentUser;
  if (!signedIn) throw new Error('Sign in again before deleting a class. Nothing was deleted.');
  const blocked: { what: string; why: string }[] = [];
  let attemptId: string | null = null;
  try {
    const kept = await archiveClassForDeleteViaServer(signedIn, id);
    // A RETRY AFTER A LOST ANSWER (refute round 1): the delete already went
    // through and this page never heard. Say so, with its Undo, never "not
    // deleted".
    if (kept.alreadyDeleted) {
      return {
        undoUntil: kept.undoUntil,
        releasedStudentIds: [],
        removedSeriesIds: [],
        cancelledReservationIds: [],
        dancersTakenOff: 0,
        weeklyTimesRemoved: 0,
        sessionsCancelled: 0,
      };
    }
    if (!kept.attemptId) throw new Error('the server did not answer an id for this delete');
    attemptId = kept.attemptId;
  } catch (e) {
    const why = e instanceof Error ? e.message : String(e);
    throw new Error(
      `${offeringName} was not deleted. Nothing was deleted, because a copy to put it back from `
      + `could not be saved first. Try again in a moment. (${why})`
    );
  }
  // A DELETE THAT STOPS CLOSES ITS COPY (refute rounds 1 and 2): the server
  // keeps what this delete reported and really took off, or drops the copy
  // when nothing came off, so Recently deleted classes never offers to put
  // back more than this delete took. Best effort: a copy this misses is
  // closed, exactly as reported, when its lease lapses.
  const stopCopy = async () => {
    if (!attemptId) return;
    try { await stopClassDeleteViaServer(signedIn, id, attemptId); } catch { /* see above */ }
  };
  // WRITE AHEAD, ONE AT A TIME (refute rounds 2 and 3, 2026-10-25). Nothing
  // comes off this class until its copy has been told, by the server, that
  // it is about to: each dancer, each weekly time, each session, ONE report
  // per removal, made just BEFORE it. Each report also confirms what came
  // off since the last one. So the copy covers every removal this page ever
  // makes, even one a tab that slept past its lease finishes after waking,
  // and a delete closed after its tab went away keeps only what was confirmed
  // plus the one removal that may have been in flight: never a dancer or a
  // session reported ahead that never came off, which Kim might then take
  // off on purpose and get back on Put back. A report the server refuses
  // (this delete was closed, put back or taken over) stops the delete:
  // nothing more comes off.
  //
  // A report counts for four minutes (the server's lease is ten). Older than
  // that, say because the phone slept mid-delete, it is made again before
  // the removal, and that report is the check that the delete is still this
  // page's to carry on.
  const COVER_FRESH_MS = 4 * 60 * 1000;
  const covered = new Map<string, number>();
  const cameOffSince = { studentIds: new Set<string>(), seriesIds: new Set<string>(), reservationIds: new Set<string>() };
  const cameOff = (kind: 'dancer' | 'series' | 'booking', itemId: string) => {
    (kind === 'dancer' ? cameOffSince.studentIds : kind === 'series' ? cameOffSince.seriesIds : cameOffSince.reservationIds).add(itemId);
  };
  let reportRefused = false;
  const coverBefore = async (kind: 'dancer' | 'series' | 'booking', itemId: string): Promise<boolean> => {
    if (reportRefused || !attemptId) return false;
    const key = `${kind}:${itemId}`;
    const at = covered.get(key);
    if (at !== undefined && Date.now() - at < COVER_FRESH_MS) return true;
    const confirmed = {
      studentIds: [...cameOffSince.studentIds],
      seriesIds: [...cameOffSince.seriesIds],
      reservationIds: [...cameOffSince.reservationIds],
    };
    const sentAt = Date.now();
    try {
      await archiveClassForDeleteViaServer(signedIn, id, attemptId, {
        studentIds: kind === 'dancer' ? [itemId] : [],
        seriesIds: kind === 'series' ? [itemId] : [],
        reservationIds: kind === 'booking' ? [itemId] : [],
      }, confirmed);
    } catch (e) {
      reportRefused = true;
      blocked.push({
        what: 'the saved copy, which could not be told what was coming off next, so nothing more was taken off',
        why: e instanceof Error ? e.message : String(e),
      });
      return false;
    }
    cameOffSince.studentIds.clear();
    cameOffSince.seriesIds.clear();
    cameOffSince.reservationIds.clear();
    // A report settles everything reported before it, so only THIS item is
    // covered now (refute round 5): one reported earlier, settled out, and
    // met again later is reported again before it comes off.
    covered.clear();
    covered.set(key, sentAt);
    return true;
  };

  // THE DANCER SIDE OF THE LINK, asked directly rather than trusted to the
  // roster. ONE array-contains clause and no orderBy, so Firestore's
  // AUTOMATIC single-field index serves it and no composite entry is needed;
  // deliberately the same shape (and the same reasoning) as the offerings
  // array-contains query in deleteStudent. The emulator does not enforce
  // composite indexes, so a where + orderBy added here would pass every test
  // and 400 against the real cloud.
  let membershipStudentIds: string[] = [];
  try {
    const membershipSnap = await getDocs(
      query(collection(firestore, 'students'), where('memberships', 'array-contains', id))
    );
    if (membershipSnap.metadata.fromCache) {
      throw new Error('the dancer list came from this device\'s saved copy, not the server');
    }
    membershipStudentIds = membershipSnap.docs.map((d) => d.id);
  } catch (e) {
    const why = e instanceof Error ? e.message : String(e);
    await stopCopy();
    throw new Error(
      `${offeringName}'s dancers could not be checked, so nothing was deleted. Nobody was taken `
      + `off the class and nothing came off the calendar. Try again in a moment. (${why})`
    );
  }

  const releasedStudentIds: string[] = [];
  const dancerIds = Array.from(new Set([...rosterStudentIds, ...membershipStudentIds]));
  for (const studentId of dancerIds) {
    if (!(await coverBefore('dancer', studentId))) break;
    try {
      await unenrollStudent(id, studentId);
      cameOff('dancer', studentId);
      releasedStudentIds.push(studentId);
    } catch (e) {
      blocked.push({ what: 'a dancer who would not come off the roster', why: e instanceof Error ? e.message : String(e) });
    }
  }

  // WEEKLY TIMES. One equality clause, automatic single-field index, the same
  // query useClassSeriesForOffering already runs on every class page.
  const removedSeriesIds: string[] = [];
  let seriesIds: string[] = [];
  try {
    const seriesSnap = await getDocs(
      query(collection(firestore, 'classSeries'), where('offeringId', '==', id))
    );
    if (seriesSnap.metadata.fromCache) {
      throw new Error('the weekly times came from this device\'s saved copy, not the server');
    }
    seriesIds = seriesSnap.docs.map((d) => d.id);
  } catch (e) {
    const why = e instanceof Error ? e.message : String(e);
    blocked.push({ what: 'this class\'s weekly times, which could not be listed', why });
  }
  // Seeded here rather than below because deleteClassSeries cancels this
  // class's generated sessions itself and reports which ones; counting them
  // again from the loose-booking read would miss them entirely, since that
  // read runs after they are already gone.
  const cancelledReservationIds: string[] = [];
  for (const seriesId of seriesIds) {
    if (reportRefused) break;
    try {
      cancelledReservationIds.push(...await deleteClassSeries(seriesId, {
        // Each session, and then the weekly time itself, told to the copy
        // before it comes off. A refusal stops this weekly time right there.
        // Being asked again means the session before this one came off.
        beforeRemove: async (step) => {
          const before = step.kind === 'series' ? step.plan : step.plan.slice(0, step.plan.indexOf(step.id));
          for (const rid of before) cameOff('booking', rid);
          const ok = step.kind === 'series'
            ? await coverBefore('series', step.id)
            : await coverBefore('booking', step.id);
          if (!ok) throw new Error('the saved copy could not be told what was coming off next');
        },
      }).then((ids) => {
        for (const rid of ids) cameOff('booking', rid);
        cameOff('series', seriesId);
        return ids;
      }));
      removedSeriesIds.push(seriesId);
    } catch (e) {
      // A WEEKLY TIME THAT STOPPED PARTWAY STILL FREED THE ROOMS IT REACHED.
      // deleteClassSeries cancels its sessions one at a time, so a refusal on
      // the third of five leaves two genuinely off the calendar; it carries
      // them out on the error for exactly this. Dropping them would make the
      // count under-report what a Director's calendar actually lost, on the
      // one path where she is most likely to look.
      cancelledReservationIds.push(...cancelledReservationIdsFromError(e));
      for (const rid of cancelledReservationIdsFromError(e)) cameOff('booking', rid);
      blocked.push({ what: 'a weekly time that would not come off', why: e instanceof Error ? e.message : String(e) });
    }
  }

  // LOOSE BOOKINGS: everything still on this class that step 2 did not reach,
  // which is every session booked by hand rather than generated by a weekly
  // time. Run AFTER the series deletes so the bookings they already cancelled
  // are simply not in this read.
  //
  // INDEX: reservations (offeringId ASC, startAt ASC), already in
  // shared/firestore.indexes.json. Checked by hand, because the emulator does
  // not enforce composite indexes and a missing one only shows up in
  // production.
  //
  // THE SAME TODAY BOUNDARY futureReservationIdsForSeries uses, through the
  // same two pieces: the query reaches back to local midnight, and
  // futureReservationIdsFromRows decides which of those rows is actually
  // still ahead. A session that met earlier today is read and left alone, and
  // a row whose startAt cannot be read is left alone rather than cancelled on
  // a guess.
  //
  // NO `kind` FILTER, DELIBERATELY, and the copy says so. A REHEARSAL always
  // carries an offeringId - shared/firestore.rules enforces it on create
  // ("rehearsal-gate: rehearsal kind requires offeringId") - so this class's
  // future rehearsals are swept here along with its sessions. That is the
  // right answer: a rehearsal for a class that no longer exists is exactly
  // the ghost this function is here to prevent, and leaving it would hold its
  // room for a class nobody can open. It is named in the Delete confirm and
  // in the how-to entry rather than done quietly, because a rehearsal
  // disappearing is a thing somebody planned around.
  let looseIds: string[] = [];
  try {
    const bookingSnap = await getDocs(query(
      collection(firestore, 'reservations'),
      where('offeringId', '==', id),
      where('startAt', '>=', FirestoreTimestamp.fromDate(startOfLocalDay(now)))
    ));
    if (bookingSnap.metadata.fromCache) {
      throw new Error('the bookings came from this device\'s saved copy, not the server');
    }
    looseIds = futureReservationIdsFromRows(
      bookingSnap.docs.map((d) => ({ id: d.id, startAt: (d.data() as { startAt?: unknown }).startAt })),
      now.getTime()
    );
  } catch (e) {
    const why = e instanceof Error ? e.message : String(e);
    blocked.push({ what: 'this class\'s bookings, which could not be listed', why });
  }
  for (const reservationId of looseIds) {
    if (!(await coverBefore('booking', reservationId))) break;
    try {
      await cancelReservation(reservationId);
      cameOff('booking', reservationId);
      cancelledReservationIds.push(reservationId);
    } catch (e) {
      blocked.push({ what: 'a session that would not come off the calendar', why: e instanceof Error ? e.message : String(e) });
    }
  }

  // TOCTOU (P2 audit fix, lane E): a dancer can be enrolled on the class page,
  // the roster card, or anywhere else `setEnrollment` runs, WHILE this whole
  // cascade above is still going - it is one Netlify round trip per dancer
  // plus one transaction per session, tens of seconds on a real class, and
  // Kim and a Director both work the Hub at once. The membership reads at
  // the very top of this function are one snapshot in that window, so a
  // late enrollment sails through untouched and this function would delete
  // the offering anyway, leaving that dancer's own memberships pointing at a
  // document that is now gone - the exact dead link lib/membershipLinks.ts
  // exists to name, with no screen left to clear it from.
  //
  // Mirrors offeringHasStrayMembership in
  // netlify/functions/assistant.mjs, which the Assistant's OWN
  // delete-empty-classes door already re-runs immediately before its write,
  // for the identical reason (that guard's own comment: "a membership can
  // appear inside the confirm window just as easily as studentIds can").
  // That check is server-only (Admin SDK); this is the client-SDK read of
  // the same two-sided link, only reached when nothing above already failed,
  // since checking memberships after a half-finished cascade would just be
  // asking a second question before answering the first.
  if (blocked.length === 0) {
    try {
      const strayFirst = await offeringStrayMembershipEntries(firestore, id);
      if (strayFirst.length > 0) {
        for (const stray of strayFirst) {
          if (!(await coverBefore('dancer', stray.id))) break;
          try {
            await unenrollStudent(id, stray.id);
            cameOff('dancer', stray.id);
            releasedStudentIds.push(stray.id);
          } catch (e) {
            blocked.push({
              what: `${stray.name} enrolled in this class while the delete was running and would not come off`,
              why: e instanceof Error ? e.message : String(e),
            });
          }
        }
        // RE-READ ONCE MORE. A successful unenroll above clears both sides of
        // the link; anything still here means either that unenroll failed
        // (already in blocked, above) or a THIRD enrollment landed in the gap
        // between it and this read. Either way the class is not deleted: the
        // whole point of this guard is to never hand a dancer a membership
        // that points at a document no longer there.
        const strayAfter = await offeringStrayMembershipEntries(firestore, id);
        for (const stray of strayAfter) {
          blocked.push({
            what: `${stray.name} is still linked to this class and would not come off`,
            why: 'a dancer enrolled while the delete was running and the class could not be made safe to remove',
          });
        }
      }
    } catch (e) {
      // Either read above throws on a cache-only answer (offline right at
      // the finish line) or on a refused query. Either way this is the LAST
      // check before the write, so an unknown here is treated the same as a
      // found one: refuse, never guess clean.
      blocked.push({
        what: 'the last check for a dancer added mid-delete, which could not be completed',
        why: e instanceof Error ? e.message : String(e),
      });
    }
  }

  if (blocked.length > 0) {
    // "The class was not deleted", never "nothing happened": by the time this
    // throws, everything that COULD be cleared already is. The same honesty
    // deleteStudent's blocked branch keeps, and for the same reason - a false
    // reassurance here sends her looking for a class state that is not there.
    //
    // AND IT NAMES WHAT ALREADY CAME OFF, counted the same way the success
    // summary counts it. The memberships are ended FIRST, before any weekly
    // time or booking is touched (the deleteStudent shape the lane spec
    // mandates), so a refusal later leaves the class alive with its roster
    // already emptied, and there is no rollback and no repair card for that:
    // the leftovers card in Studio Settings repairs bookings and orphaned
    // weekly times, never a membership. A Director who reads this, gives up
    // and keeps the class must be told the dancers are off, or she keeps a
    // live class whose roster was silently wiped.
    await stopCopy();
    throw new Error(deleteOfferingBlockedCopy(offeringName, blocked, {
      dancersTakenOff: releasedStudentIds.length,
      weeklyTimesRemoved: removedSeriesIds.length,
      sessionsCancelled: cancelledReservationIds.length,
    }));
  }

  // THE CLASS GOES LAST, THROUGH THE SERVER (bug B22): it re-checks that no
  // dancer and no upcoming booking still hangs on the class, deletes it, and
  // marks the copy kept above as deleted, with its one-hour Undo and its
  // thirty days. Every dancer released here is named so a put back returns
  // the one caught by the last check too. IDEMPOTENT, like deleteStudent: a
  // retry after a lost answer finds the copy already deleted and answers the
  // same.
  let finished: { undoUntil: number | null };
  try {
    finished = await finishClassDeleteViaServer(signedIn, id, releasedStudentIds, attemptId ?? '');
  } catch (e) {
    await stopCopy();
    throw e;
  }
  return {
    undoUntil: finished.undoUntil,
    releasedStudentIds,
    removedSeriesIds,
    cancelledReservationIds,
    dancersTakenOff: releasedStudentIds.length,
    weeklyTimesRemoved: removedSeriesIds.length,
    sessionsCancelled: cancelledReservationIds.length,
  };
}

/**
 * Subscribe to a single offering by id. Returns null while loading or if
 * the doc doesn't exist; surfaces any rule rejection via `error`.
 */
export function useOffering(id: string | undefined): {
  loading: boolean;
  offering: OfferingDoc | null;
  error: string | null;
} {
  const [offering, setOffering] = useState<OfferingDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!db || !id) {
      setOffering(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = onSnapshot(
      doc(db, 'offerings', id),
      (snap) => {
        if (snap.exists()) {
          setOffering(normalizeOfferingDoc(snap.id, snap.data()));
        } else {
          setOffering(null);
        }
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [id]);

  return { loading, offering, error };
}

/**
 * Enroll a student in an offering. Updates both sides of the membership link
 * in a single batch so offering.studentIds and student.memberships stay in
 * sync. arrayUnion is idempotent - a double-tap won't duplicate the entry.
 */
export async function enrollStudent(
  offeringId: string,
  studentId: string
): Promise<void> {
  await setEnrollment(offeringId, studentId, 'enroll');
}

/** What the server says it actually did, for a caller that has to report it. */
export interface SetEnrollmentResult {
  /**
   * The class was already gone, so this call cleared the dancer's dead link
   * instead of taking them off a live roster. The server decides this, not the
   * page: only it holds the real document. Bobbie, 2026-09-02, on a dancer
   * whose card said "No classes linked yet" while Delete said "off 2 classes"
   * - two guesses about the same dancer, neither of them the server's answer.
   */
  offeringAlreadyDeleted: boolean;
}

/**
 * Unenroll a student from an offering. Both sides of the membership link are
 * cleared in a single batch. A class that no longer exists is not an error
 * here: the dancer's dead link is cleared and the result says so.
 */
export async function unenrollStudent(
  offeringId: string,
  studentId: string
): Promise<SetEnrollmentResult> {
  return setEnrollment(offeringId, studentId, 'unenroll');
}

async function setEnrollment(
  offeringId: string,
  studentId: string,
  action: 'enroll' | 'unenroll'
): Promise<SetEnrollmentResult> {
  const me = getAuth().currentUser;
  if (!me) throw new Error('Sign in required.');
  const idToken = await me.getIdToken();
  const res = await fetch('/.netlify/functions/setEnrollment', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
    body: JSON.stringify({ offeringId, studentId, action }),
  });
  const contentType = res.headers.get('content-type') || '';
  const data = contentType.includes('application/json')
    ? ((await res.json().catch(() => ({}))) as {
        error?: string;
        offeringAlreadyDeleted?: boolean;
      })
    : {};
  const responseText = contentType.includes('application/json')
    ? ''
    : await res.text().catch(() => '');
  if (!res.ok || data.error) {
    if (data.error) {
      const lower = data.error.toLowerCase();
      if (lower.includes('server not configured') || lower.includes('firebase_service_account')) {
        throw new Error('The studio server setup is missing its Firebase connection in Netlify. Roster changes will work after that key is added, then refresh this class and try Add again.');
      }
      throw new Error(data.error);
    }
    if (responseText.includes('Password protected site')) {
      throw new Error('Roster update was blocked by the preview password. Refresh the preview, enter the password, and try Add again.');
    }
    if (res.status === 504) {
      throw new Error('The roster update took too long. Refresh the class and try Add again.');
    }
    if (res.status === 502) {
      throw new Error('The roster updater hit a server error before it could finish. Refresh the class and try Add again. If it repeats, submit an error report from the error screen.');
    }
    throw new Error(`The roster update did not finish. Server returned ${res.status}. Refresh the class and try again.`);
  }
  // Absent means false, and an older function build that does not send the
  // field reads as "a real class was updated" rather than inventing a ghost.
  return { offeringAlreadyDeleted: data.offeringAlreadyDeleted === true };
}

// ---------------------------------------------------------------------------
// Alternates
//
// The arithmetic lives in lib/alternates.ts, which imports nothing so it can be
// unit tested. Everything below is the Firestore side of it: reading the
// class's attendance marks, and the two Director writes.
// ---------------------------------------------------------------------------

/** Is this dancer currently standing by on this dance? */
export function isAlternate(
  offering: Pick<OfferingDoc, 'alternateStudentIds'>,
  studentId: string
): boolean {
  return (offering.alternateStudentIds ?? []).includes(studentId);
}

/** Why, or null when they are a full dancer on this dance. */
export function alternateReasonFor(
  offering: Pick<OfferingDoc, 'alternateStudentIds' | 'alternateInfo'>,
  studentId: string
): AlternateReason | null {
  if (!isAlternate(offering, studentId)) return null;
  // Falls back to 'assigned' when the record is missing or unreadable, which
  // is the SAFE default rather than the tidy one: it renders with no warning
  // color and no absence count, so a lost record can never make a dancer look
  // like they are in trouble when nothing says they are.
  return offering.alternateInfo?.[studentId]?.reason ?? 'assigned';
}

// FIXED, 2026-09-17, refute round one: this used to build the date key off
// the BROWSER'S OWN clock fields, and the server (attendanceConcernsLocal.mjs)
// built it off UTC, so a reinstatement stamped after 7pm Central (6pm in
// winter) read as tomorrow on the server and today here. The baseline is
// EXCLUSIVE, so the two sides forgave a different number of classes and a
// real concern could vanish from the server's list while still showing here.
// A viewer's own phone clock is not the right fix either - the day that
// matters is the STUDIO'S, a constant, so both sides now read it through the
// one shared helper.
function timestampToLocalDate(value: unknown): string | null {
  return centralCivilDateFromTimestamp(value);
}

/**
 * The date past which a dancer's missed-rehearsal streak starts counting again.
 *
 * This is the whole reason a reinstated dancer's alternateInfo entry is kept
 * rather than deleted. Without it, the same three absences that got them moved
 * are still the three newest marks the morning after Kim approves them back,
 * so they would be re-flagged immediately and she would fight it every week.
 * Null once they have never been reinstated, which means the full lookback.
 */
export function alternateStreakBaseline(
  offering: Pick<OfferingDoc, 'alternateInfo'>,
  studentId: string
): string | null {
  const info = offering.alternateInfo?.[studentId];
  if (!info || !info.reinstatedAt) return null;
  return timestampToLocalDate(info.reinstatedAt);
}

/** One attendance record as the alternate maths and the step-in note need it. */
export interface ClassAttendanceMark extends AlternateMark {
  studentId: string;
  notes: string;
}

export interface UseClassAttendanceMarksResult {
  loading: boolean;
  /** studentId to their marks for this class, newest last. */
  byStudent: Map<string, ClassAttendanceMark[]>;
  error: string | null;
}

/**
 * The same two-listener join as createAttendanceDayJoin, for the shape this
 * hook returns: a LIST of marks per student rather than one mark.
 *
 * It cannot share that function because the key is derived differently. There
 * the note id is the mark's own document id, which the snapshot hands over.
 * Here the query returns a window of dates and this hook never reads the
 * document ids, so the id has to be rebuilt - attendanceDocId(offeringId,
 * date, studentId), the same composite the writer and the notes collection
 * use. Getting that wrong returns '' for every mark, which is exactly the bug
 * a review found: RosterPanel hands this object to the alternate step-in
 * writer, which APPENDS to what it is given, so a blank note meant recording a
 * cover silently wrote over the teacher's sentence.
 *
 * The emit gate is load-bearing for the same reason it is there - see
 * createAttendanceDayJoin.
 */
export function createClassMarksJoin(
  offeringId: string,
  emit: (byStudent: Map<string, ClassAttendanceMark[]>) => void
): {
  setMarks: (next: Map<string, ClassAttendanceMark[]>) => void;
  setNotes: (next: Map<string, string>) => void;
  notesUnavailable: () => void;
} {
  let marksByStudent = new Map<string, ClassAttendanceMark[]>();
  let notesById = new Map<string, string>();
  let notesReported = false;
  const publish = () => {
    if (!notesReported) return;
    const merged = new Map<string, ClassAttendanceMark[]>();
    for (const [studentId, list] of marksByStudent) {
      merged.set(studentId, list.map((m) => ({
        ...m,
        notes: notesById.get(attendanceDocId(offeringId, m.date, studentId)) ?? '',
      })));
    }
    emit(merged);
  };
  return {
    setMarks: (next) => { marksByStudent = next; publish(); },
    setNotes: (next) => { notesById = next; notesReported = true; publish(); },
    notesUnavailable: () => { notesReported = true; publish(); },
  };
}

function isoDaysAgoLocal(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Every attendance mark for ONE class inside the lookback window.
 *
 * Scoped to a single offering on purpose. Merging two classes' marks into one
 * timeline invents runs of absences that never happened at any single dance,
 * and would move a dancer out of a dance they have not missed once. It lives
 * beside the offering rather than in lib/attendance.ts because alternate
 * standing is a property of the dance, and the existing recent-attendance hook
 * returns counts, not the dates the evidence needs.
 */
export function useClassAttendanceMarks(
  offeringId: string | undefined,
  windowDays = ALTERNATE_LOOKBACK_DAYS,
  enabled = true
): UseClassAttendanceMarksResult {
  const [byStudent, setByStudent] = useState<Map<string, ClassAttendanceMark[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!db || !offeringId || !enabled) {
      setByStudent(new Map());
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const q = query(
      collection(db, 'attendance'),
      where('offeringId', '==', offeringId),
      where('date', '>=', isoDaysAgoLocal(windowDays))
    );
    // THE TEACHER'S NOTE IS NOT ON THIS DOCUMENT ANY MORE. It lives on
    // attendanceNotes/{sameId} so a family can read a mark without reading the
    // words (Bobbie, 2026-08-22). Reading `raw.notes` here returned '' for
    // every mark, and because RosterPanel hands this object straight to the
    // alternate step-in writer - which APPENDS to what it is given - recording
    // a cover silently wrote over whatever the teacher had written. A review
    // proved it. subscribeAttendanceNotes is the one place that knows.
    const join = createClassMarksJoin(offeringId, setByStudent);
    const unsubNotes = subscribeAttendanceNotes(
      offeringId,
      { fromDate: isoDaysAgoLocal(windowDays) },
      (map: Map<string, string>) => join.setNotes(map),
      () => join.notesUnavailable()
    );
    const unsub = onCountableMarks(
      q,
      isoDaysAgoLocal(windowDays),
      (snap) => {
        const next = new Map<string, ClassAttendanceMark[]>();
        for (const d of snap.docs) {
          const raw = d.data() as Record<string, unknown>;
          const studentId = typeof raw.studentId === 'string' ? raw.studentId : '';
          const date = typeof raw.date === 'string' ? raw.date : '';
          const status = raw.status as AlternateMark['status'];
          if (!studentId || !date) continue;
          const list = next.get(studentId) ?? [];
          list.push({ studentId, date, status, notes: '' });
          next.set(studentId, list);
        }
        for (const list of next.values()) list.sort((a, b) => a.date.localeCompare(b.date));
        join.setMarks(next);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => {
      unsub();
      unsubNotes();
    };
  }, [offeringId, windowDays, enabled]);

  return { loading, byStudent, error };
}

/**
 * How misses are counted on this offering. Kim's rule (2026-07-31): a comp
 * dance flips on 3 missed TOTAL for that single dance, not 3 in a row; a
 * recreational class keeps the consecutive rule. Uses the same comp heuristic
 * every other screen uses (explicit kind first, legacy name fallback) so the
 * count matches what the offering's own pill says it is.
 */
export function alternateCountModeFor(offering: OfferingLabelInput): AlternateCountMode {
  return isCompetitionOffering(offering) ? 'total' : 'consecutive';
}

/** The class's real meeting sequence, for callers that know the schedule. */
export interface AlternateStreakCalendar {
  /** Every date this dance met in the window: its recurring times minus closures, from expandMeetingDates. */
  meetingDates?: readonly string[];
  /** Every date this dance registered any dancer at all. */
  registerDates?: readonly string[];
}

/**
 * The missed-rehearsal count for one dancer at one dance, with their own
 * reinstatement baseline already applied and the offering's own count mode
 * (comp = total, rec = consecutive).
 *
 * `calendar` is optional because not every caller has the schedule in hand,
 * and computeAlternateStreak is honest without one: it simply counts the rows
 * that exist. A caller that DOES have it gets the rec "in a row" rule counted
 * against real meetings, so a dancer with no row on a night the register was
 * taken reads as unknown rather than joining two absences into a run.
 */
export function alternateStreakFor(
  offering: Pick<OfferingDoc, 'alternateInfo' | 'kind' | 'name' | 'style'>,
  studentId: string,
  marks: readonly ClassAttendanceMark[] | undefined,
  nowMs: number,
  calendar?: AlternateStreakCalendar
): AlternateStreak {
  return computeAlternateStreak(marks ?? [], {
    nowMs,
    sinceDate: alternateStreakBaseline(offering, studentId),
    mode: alternateCountModeFor(offering),
    meetingDates: calendar?.meetingDates,
    registerDates: calendar?.registerDates,
  });
}

export interface SetAlternateInput {
  offeringId: string;
  studentId: string;
  reason: AlternateReason;
  /** The dates behind an attendance move. Ignored for an assigned alternate. */
  causedByDates?: readonly string[];
  /** uid of the Director making the call. Rules only accept a Director here. */
  setBy: string;
}

/**
 * Mark a dancer as standing by on this dance.
 *
 * Director-only at the rules. A teacher tapping an attendance chip must never
 * set alternate standing as a side effect: an attendance screen is a fast,
 * mis-tappable surface, and a casting change is not something anybody should
 * be able to trigger by fat-fingering a row.
 */
export async function setStudentAlternate(input: SetAlternateInput): Promise<void> {
  if (!isSafeDocIdKey(input.studentId)) throw new Error('That dancer id cannot be used here.');
  // Through the alternateStanding Function since 2026-11-03, so the comp
  // alternate report has a who-and-when trail: the server writes the SAME
  // alternateInfo patch this function used to write directly, plus one
  // alternateEvents log entry, in one transaction. `setBy` is the caller's
  // verified sign-in on the server, never a field the client chooses.
  const { moveToAlternateLogged } = await import('./alternateStandingClient');
  await moveToAlternateLogged({
    offeringId: input.offeringId,
    studentId: input.studentId,
    reason: input.reason,
    causedByDates: input.causedByDates,
  });
}

export interface ReinstateAlternateInput {
  offeringId: string;
  studentId: string;
  /** Kim's optional short note: "learned the combo, made up Tuesday". */
  note?: string;
  reinstatedBy: string;
}

/**
 * Kim approves a dancer back to a full spot on the dance.
 *
 * Clears the standing AND resets the streak baseline in the same write, by
 * stamping reinstatedAt on the record rather than deleting it. See
 * alternateStreakBaseline for why deleting would put them straight back.
 */
export async function reinstateStudentFromAlternate(
  input: ReinstateAlternateInput
): Promise<void> {
  if (!isSafeDocIdKey(input.studentId)) throw new Error('That dancer id cannot be used here.');
  // Logged through the alternateStanding Function, same as setStudentAlternate.
  const { reinstateFromAlternateLogged } = await import('./alternateStandingClient');
  await reinstateFromAlternateLogged({
    offeringId: input.offeringId,
    studentId: input.studentId,
    note: input.note,
  });
}

// ---------------------------------------------------------------------------
// Recipient chip summarization (task #72)
//
// Bobbie: recipient chip lists get absurd with 50 dancers in a class. Any
// surface that renders "For dancer" chips or assignee/"Team member" chips
// should collapse to one compact line once there are 5 or more, instead of
// a wall of tiny pills. Below 5 it keeps showing individual name chips like
// today. Tasks.tsx, the family dancer profile, and the Landing family
// attention rows all call these two functions so the behavior (and the
// threshold) stays identical everywhere.
// ---------------------------------------------------------------------------

/** Below this many recipients we still show individual name chips. */
const RECIPIENT_CHIP_THRESHOLD = 5;

export type RecipientSummary =
  | { kind: 'names'; names: string[] }
  | { kind: 'summary'; label: string };

function uniqueNonEmpty(values: readonly string[] | null | undefined): string[] {
  return Array.from(
    new Set((values ?? []).filter((v): v is string => typeof v === 'string' && v.trim().length > 0))
  );
}

/** True when every id in `idSet` is present in the offering's roster. */
function offeringCoversStudentIds(offering: Pick<OfferingDoc, 'studentIds'>, idSet: Set<string>): boolean {
  if (idSet.size === 0) return false;
  const rosterIds = new Set(offering.studentIds);
  for (const id of idSet) {
    if (!rosterIds.has(id)) return false;
  }
  return true;
}

/**
 * Find the class whose roster equals or is a superset of `idSet`. Prefers
 * `preferredOfferingId` (the task's own source.offeringId, when it has one)
 * as long as that class's roster actually covers every id; otherwise picks
 * the smallest covering roster so a whole-class fanout matches its own
 * class rather than some larger class that happens to contain everyone.
 */
function findMatchingOffering(
  idSet: Set<string>,
  offerings: readonly OfferingDoc[],
  preferredOfferingId?: string | null
): OfferingDoc | null {
  if (idSet.size === 0) return null;
  if (preferredOfferingId) {
    const preferred = offerings.find((o) => o.id === preferredOfferingId);
    if (preferred && offeringCoversStudentIds(preferred, idSet)) return preferred;
  }
  let best: OfferingDoc | null = null;
  for (const offering of offerings) {
    if (!offeringCoversStudentIds(offering, idSet)) continue;
    if (!best || offering.studentIds.length < best.studentIds.length) best = offering;
  }
  return best;
}

/**
 * Turn a task/message's studentIds into either individual dancer name chips
 * (fewer than 5 dancers) or one compact summary line (5+): "To: [Class
 * name]" when the ids line up with a class roster the caller passed in
 * (matched via the task's own offeringId first, else any covering class),
 * otherwise "To: N dancers".
 */
export function summarizeRecipients(
  studentIds: readonly string[] | null | undefined,
  offerings: readonly OfferingDoc[],
  dancerNameById: (studentId: string) => string,
  opts?: { offeringId?: string | null }
): RecipientSummary {
  const ids = uniqueNonEmpty(studentIds);
  if (ids.length === 0) return { kind: 'names', names: [] };
  if (ids.length < RECIPIENT_CHIP_THRESHOLD) {
    return { kind: 'names', names: ids.map(dancerNameById) };
  }
  const match = findMatchingOffering(new Set(ids), offerings, opts?.offeringId);
  return { kind: 'summary', label: match ? `To: ${match.name}` : `To: ${ids.length} dancers` };
}

/**
 * Same idea for assignee/"Team member" chips: 5+ assignees collapses to one
 * line instead of a wall of chips. When the assignees came from a whole-
 * class fanout (the task also carries the matching class's studentIds),
 * names the class plus "families"; otherwise just says how many people.
 */
export function summarizeAssignees(
  assigneeUids: readonly string[] | null | undefined,
  offerings: readonly OfferingDoc[],
  userNameById: (uid: string) => string,
  opts?: { studentIds?: readonly string[] | null; offeringId?: string | null }
): RecipientSummary {
  const uids = uniqueNonEmpty(assigneeUids);
  if (uids.length === 0) return { kind: 'names', names: [] };
  if (uids.length < RECIPIENT_CHIP_THRESHOLD) {
    return { kind: 'names', names: uids.map(userNameById) };
  }
  const studentIds = uniqueNonEmpty(opts?.studentIds);
  const match = studentIds.length > 0 ? findMatchingOffering(new Set(studentIds), offerings, opts?.offeringId) : null;
  return {
    kind: 'summary',
    label: match ? `To: ${match.name} families` : `To: ${uids.length} people`,
  };
}
